/* ============================================================
   RSI USED PALLET RACKING QUOTE BUILDER — v2 (wizard rebuild)
   assets/rsi-uqb-v2.js
   pricingVersion: "2026-08-v2"  (engine section below is a verbatim
   port of assets/rsi-used-racking-quote-builder.js — same tables,
   same functions, same state shape — not a rewrite. Ported so v2
   can load standalone without the old main script.)

   Rebuilt 2026-08-06 to match the prototype's ACTUAL model (single
   stage container, content swapped by render(); state lives in a
   persistent JS object, not the DOM) after the first port's hybrid
   "everything permanently in the DOM, toggle hidden" approach turned
   out broken: updateConditionalFields() never set stepEl.hidden, and
   several data-rsi-uqb-field names (brand, requestType, ...) are
   reused across the 5 "size" variants, so with every variant's
   fields simultaneously live in the DOM, fieldValue() could return
   whichever matching radio happened to be checked in ANY variant —
   a genuine state collision, not a styling bug. Only one stage's
   markup exists in the DOM at a time now, so that class of bug is
   gone by construction.

   Contract this file MUST keep, because assets/rsi-uqb-phase1.js
   (loaded after this file, UNCHANGED, not edited as part of this
   rebuild) depends on it:
     - root: [data-rsi-uqb]
     - form: id="UsedRackingQuoteBuilder", wrapped in [data-rsi-uqb-form]
     - posted marker: [data-rsi-uqb-posted]
     - hidden fields: contact[form_context], contact[request_type]
       [data-rsi-uqb-hidden-request-type], contact[guide_price_ex_vat]
       [data-rsi-uqb-hidden-ex-vat], contact[guide_price_inc_vat]
       [data-rsi-uqb-hidden-inc-vat], contact[quote_payload_json]
       [data-rsi-uqb-payload-field], contact[body] [data-rsi-uqb-body-field]
     - contact[quote_ref] [data-rsi-p1-ref-field]
     - every field rendered carries data-rsi-uqb-field="<path>" using
       the SAME path vocabulary as before (requestType, matchingExisting,
       brand, cr.bays, cr.frameHeightMm, ..., customer.name,
       delivery.postcode, acknowledged, ...) — phase-1's
       fieldEl()/radioText()/selText() query these paths directly.
       Only the CURRENT stage's fields exist at any moment, but that's
       fine: phase-1 only ever reads them at submit time, and the
       Send stage (with the customer.* fields) is always what's
       rendered when the submit button exists at all.
     - [data-rsi-uqb-summary-desktop] / [data-rsi-uqb-summary-mobile]:
       containers phase-1 stamps a ref line onto and reads
       innerText/innerHTML from for the webhook payload. These stay
       permanent shell elements (outside the stage pane); the ledger
       is rendered into them.
     - [data-rsi-uqb-success] success panel containing
       [data-rsi-p1-ref] [data-rsi-p1-time] [data-rsi-p1-slot]
       [data-rsi-p1-recap] [data-rsi-p1-matching] [data-rsi-p1-sms-note]
       [data-rsi-p1-sla-inline] — phase-1's populateReceipt()/paintSla()
       write into these; this file never writes to them itself.
     - [data-rsi-p1-callback] radio group for callback preference
       (rendered as part of the Send stage).
     - submit -> pending-marker -> posted-state resolution pattern is
       reused byte-for-byte from the existing main script.

   guide_price_ex_vat / guide_price_inc_vat are left UNPOPULATED here,
   exactly matching current production behaviour (flagged separately,
   not silently "fixed").
   ============================================================ */
(function () {
  'use strict';

  /* ============================================================
     PRICING ENGINE — verbatim port, pricingVersion 2026-08-v2.
     No logic changes from assets/rsi-used-racking-quote-builder.js.
     ============================================================ */
  var VAT_RATE = 0.20;

  var FRAME_PRICES = {
    link51: {
      900: { 2000: 7000, 3000: 7500, 4000: 8000, 5000: 9000, 5500: 9500, 6000: 10000, 7800: 12000 },
      1100: { 2000: 6000, 3000: 7000, 4000: 8000, 5000: 9000, 6000: 10000 }
    },
    redirack: {
      900: { 2000: 4400, 3000: 5600, 4000: 6800, 5000: 8000, 6000: 9200 }
    },
    dexion_p90: {
      900: { 2000: 5000, 3000: 6000, 4000: 7000 }
    },
    mecalux: {
      900: { 2000: 6000, 3000: 7000, 4000: 8000, 5000: 9000, 6000: 10000, 7000: 11000, 8000: 12000, 9000: 13000 },
      1100: { 2000: 7000, 3000: 8000, 4000: 9000 }
    },
    hilo: {
      1100: { 6000: 9000, 7000: 10000, 8000: 11000, 9000: 12000, 10000: 13000 }
    },
    pss: {
      1000: { any: 3500 },
      600: { any: 4500 }
    }
  };

  var BEAM_PRICES = {
    link51: {
      1200: { priceEach: 1000, swlKg: 1000 },
      1815: { priceEach: 1250, swlKg: 1000 },
      2225: { priceEach: 1500, swlKg: 1200, condition: 'used' },
      2300: { priceEach: 1500, swlKg: 2000 },
      2470: { priceEach: 1200, swlKg: 500 },
      2700: { priceEach: 1500, swlKg: 1200 },
      3300: { priceEach: 2000, swlKg: 1500 },
      3750: { priceEach: 2250, swlKg: 3000 }
    },
    redirack: { 2700: { priceEach: 1200, swlKg: 2000 } },
    dexion_p90: { 2700: { priceEach: 1000, swlKg: 1000 }, 3300: { priceEach: 1000, swlKg: 750 } },
    mecalux: { 2700: { priceEach: 1750, swlKg: 3000 }, 2250: { priceEach: 1500, swlKg: null } },
    hilo: { 3300: { priceEach: 1500, swlKg: null } },
    schaefer: { 2700: { priceEach: 1500, swlKg: 2000 } }
  };

  var DECKING_PRICES = {
    none: { 900: 0, 1100: 0 },
    timber: { 900: 3500, 1100: 4500 },
    chipboard: { 900: 3500, 1100: 4500 },
    mesh: { 900: 4400, 1100: 5400 }
  };

  var ACCESSORY_PRICES = {
    floorBoltEach: 100,
    frameLegProtectorEach: 2913,
    loadNoticeBoardEach: 2500,
    endBarrierSingle: null,
    endBarrierDouble: null,
    lockingPinEach: 100
  };

  var BRAND_LABELS = {
    link51: 'Link 51', redirack: 'Redirack', dexion_p90: 'Dexion P90', mecalux: 'Mecalux',
    hilo: 'Hilo', schaefer: 'Schaefer', pss: 'PSS', dexion_speedlock: 'Dexion Speedlock',
    other: 'Other', not_sure: 'Not sure'
  };

  var BEAM_LENGTH_LABELS = {
    1200: '1200mm', 1815: '1815mm', '2225_2250': '2225mm / 2250mm', 2300: '2300mm',
    2470: '2470mm', 2700: '2700mm', 3300: '3300mm', 3750: '3750mm', other: 'Other / Not sure'
  };

  var DELIVERY_REQUIREMENT_LABELS = {
    supply_only: 'Supply only', supply_install: 'Supply & installation', survey_not_sure: 'Site survey / not sure'
  };

  var DECKING_TYPE_LABELS = {
    none: 'None', timber: 'Timber decks', chipboard: 'Chipboard decks', mesh: 'Galvanised mesh decks', not_sure: 'Not sure'
  };

  var PALLETS_PER_LEVEL = {
    1200: { uk_standard: 1, euro: 1, mixed: 1 },
    1815: { uk_standard: 1, euro: 2, mixed: 2 },
    '2225_2250': { uk_standard: 2, euro: 2, mixed: 2 },
    2300: { uk_standard: 2, euro: 2, mixed: 2 },
    2470: { uk_standard: 2, euro: 2, mixed: 2 },
    2700: { uk_standard: 2, euro: 3, mixed: 3 },
    3300: { uk_standard: 3, euro: 3, mixed: 3 },
    3750: { uk_standard: 3, euro: 4, mixed: 4 }
  };

  function round2(pence) { return Math.round(pence); }
  function calcVat(exVatPence) { return round2(exVatPence * VAT_RATE); }

  var CURRENCY_FORMATTER = (typeof Intl !== 'undefined' && Intl.NumberFormat)
    ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
    : null;

  function money(pence) {
    if (pence === null || pence === undefined) return 'Price to confirm';
    var pounds = pence / 100;
    return CURRENCY_FORMATTER ? CURRENCY_FORMATTER.format(pounds) : ('£' + pounds.toFixed(2));
  }

  function resolveBeamLengthKeyForBrand(beamLengthKey, brand) {
    if (beamLengthKey !== '2225_2250') return beamLengthKey;
    if (brand === 'link51') return '2225';
    if (brand === 'mecalux') return '2250';
    return null;
  }

  function calculateCompleteRunQuantities(cr) {
    var bays = Number(cr.bays) || 0;
    var beamLevels = cr.beamLevels === 'not_sure' ? null : Number(cr.beamLevels) || 0;
    var frames = bays > 0 ? bays + 1 : 0;
    var beamPairs = (bays > 0 && beamLevels) ? bays * beamLevels : null;
    var totalBeams = beamPairs !== null ? beamPairs * 2 : null;
    var lockingPins = totalBeams !== null ? totalBeams * 2 : null;
    var deckingPositions = (bays > 0 && beamLevels) ? bays * beamLevels : null;
    var floorBolts = cr.floorBolts === 'yes' && frames > 0 ? frames * 4 : 0;
    var protectors = 0;
    if (frames > 0) {
      if (cr.frameProtectors === 'front') protectors = frames;
      else if (cr.frameProtectors === 'all') protectors = frames * 2;
    }
    var loadNotices = cr.loadNotice === 'yes' ? 1 : 0;
    var endBarrierQty = cr.endBarriers === 'single' ? 1 : cr.endBarriers === 'double' ? 2 : 0;

    return {
      frames: frames, beamPairs: beamPairs, totalBeams: totalBeams, lockingPins: lockingPins,
      deckingPositions: deckingPositions, floorBolts: floorBolts, protectors: protectors,
      loadNotices: loadNotices, endBarrierQty: endBarrierQty
    };
  }

  function calculateFramesOnlyQuantities(fo) { return { frames: Number(fo.quantity) || 0 }; }

  function calculateBeamsOnlyQuantities(bo) {
    var pairs = Number(bo.beamPairQuantity) || 0;
    var totalBeams = pairs * 2;
    var lockingPins = bo.includeLockingPins === 'yes' ? totalBeams * 2 : null;
    return { beamPairs: pairs, totalBeams: totalBeams, lockingPins: lockingPins };
  }

  function calculateDeckingOnlyQuantities(dOnly) { return { deckingPositions: (Number(dOnly.bays) || 0) * (Number(dOnly.levels) || 0) }; }

  /* Category-gated: only the currently-selected category's formula
     produces a non-zero value. Required because floorBoltFrames/
     protectorFrames/pinsLevels all carry non-zero Phase-1 defaults
     (so their steppers show a sensible starting number) and
     barrierRunEnds always resolves to 1 or 2 (no "none" option) — an
     ungated version would show every category's line simultaneously
     regardless of which one is actually selected. */
  function calculateAccessoriesOnlyQuantities(ao) {
    var floorBolts = 0, protectors = 0, endBarrierQty = 0, lockingPins = 0;
    if (ao.category === 'floor_bolts') {
      floorBolts = (Number(ao.floorBoltFrames) || 0) * 4;
    } else if (ao.category === 'leg_protectors') {
      var f = Number(ao.protectorFrames) || 0;
      protectors = ao.protectorCoverage === 'all' ? f * 2 : f;
    } else if (ao.category === 'barriers') {
      endBarrierQty = ao.barrierRunEnds === 'both' ? 2 : 1;
    } else if (ao.category === 'locking_pins') {
      lockingPins = (Number(ao.pinsLevels) || 0) * 4;
    }
    return {
      floorBolts: floorBolts, protectors: protectors,
      loadNotices: Number(ao.loadNoticeQuantity) || 0,
      endBarrierQty: endBarrierQty, lockingPins: lockingPins
    };
  }

  function resolvePricingBrand(brand) {
    if (brand === 'other' || brand === 'not_sure') return { brand: 'link51', isFallback: true };
    return { brand: brand, isFallback: false };
  }

  function resolveFramePrice(brand, depthMm, heightMm) {
    var resolved = resolvePricingBrand(brand);
    var table = FRAME_PRICES[resolved.brand];
    if (!table) return { unitPricePence: null, reason: 'no_table' };
    var depthTable = table[depthMm];
    if (!depthTable) return { unitPricePence: null, reason: 'unsupported_depth' };
    if (depthTable.any !== undefined) return { unitPricePence: depthTable.any, reason: null, flatAnyHeight: true, isFallback: resolved.isFallback };
    var price = depthTable[heightMm];
    if (price === undefined) return { unitPricePence: null, reason: 'unsupported_height' };
    return { unitPricePence: price, reason: null, isFallback: resolved.isFallback };
  }

  function resolveBeamPrice(brand, beamLengthKeyRaw) {
    var resolved = resolvePricingBrand(brand);
    var resolvedKey = resolveBeamLengthKeyForBrand(beamLengthKeyRaw, resolved.brand);
    if (resolvedKey === null) return { unitPricePence: null, swlKg: null, reason: 'unresolved_length' };
    var table = BEAM_PRICES[resolved.brand];
    if (!table) return { unitPricePence: null, swlKg: null, reason: 'no_table' };
    var entry = table[resolvedKey];
    if (!entry) return { unitPricePence: null, swlKg: null, reason: 'unsupported_length' };
    return { unitPricePence: entry.priceEach, swlKg: entry.swlKg, reason: null, condition: entry.condition, isFallback: resolved.isFallback };
  }

  function resolveDeckingPrice(beamLengthKeyRaw, deckingType, depthMm) {
    if (!deckingType || deckingType === 'none' || deckingType === 'not_sure') return { pricePerPosition: null, reason: 'not_applicable' };
    if (beamLengthKeyRaw !== '2700') return { pricePerPosition: null, reason: 'unsupported_beam_length' };
    var table = DECKING_PRICES[deckingType];
    if (!table) return { pricePerPosition: null, reason: 'unknown_type' };
    var price = table[depthMm];
    if (price === undefined) return { pricePerPosition: null, reason: 'unsupported_depth' };
    return { pricePerPosition: price, reason: null };
  }

  function checkBeamCapacity(state, beamPriceResult) {
    if (state.requestType !== 'complete_run') return null;
    var cr = state.completeRun;
    if (!cr) return null;
    var maxLoad = Number(cr.maxLoadPerLevelKg);
    if (!maxLoad) return null;
    if (!beamPriceResult || !beamPriceResult.swlKg) return null;
    if (maxLoad > beamPriceResult.swlKg) {
      return { swlKg: beamPriceResult.swlKg };
    }
    return null;
  }

  function getWarnings(state, beamPriceResult) {
    var warnings = [];
    function add(key, text) {
      if (!warnings.some(function (w) { return w.key === key; })) warnings.push({ key: key, text: text });
    }

    add('delivery_excluded', 'Guide price excludes delivery and installation.');

    if (state.matchingExisting === 'yes' || state.matchingExisting === 'not_sure') {
      add('compatibility', 'Brand compatibility must be confirmed before final quote.');
    }

    var brand = state.brand;
    var cr = state.completeRun;

    if (brand === 'mecalux' && cr && Number(cr.frameDepthMm) === 900 && Number(cr.bays) < 20) {
      add('mecalux_900', 'Mecalux 900mm guide pricing may require a larger order. Team review needed.');
    }
    if (brand === 'hilo' && cr && Number(cr.frameHeightMm) < 6000) {
      add('hilo_height', 'Hilo guide pricing starts from 6000mm-high frames. Team review needed.');
    }
    if (brand === 'pss') {
      add('pss_frames_only', 'PSS guide data covers frames only. Beam and complete-system pricing requires review.');
    }
    if (brand === 'schaefer') {
      add('schaefer_beams_only', 'Schaefer guide data currently covers beams only. Frame pricing requires review.');
    }
    if (brand === 'dexion_speedlock') {
      add('speedlock_manual', 'Price to confirm.');
    }
    if (brand === 'other' || brand === 'not_sure') {
      add('fallback_basis', 'Guide estimate currently based on Link 51 pricing. Brand compatibility and final system choice must be confirmed.');
    }

    var beamKey = state.requestType === 'complete_run' ? (cr && cr.beamLengthKey)
      : state.requestType === 'beams_only' ? (state.beamsOnly && state.beamsOnly.beamLengthKey)
      : state.requestType === 'decking_only' ? (state.deckingOnly && state.deckingOnly.beamLengthKey)
      : null;
    if (beamKey === '2225_2250') {
      add('beam_2225_2250', 'Exact 2225mm or 2250mm beam length must be confirmed before the final quote.');
    }

    if (state.requestType === 'complete_run' && cr && cr.decking && cr.decking !== 'none' && cr.decking !== 'not_sure' && cr.beamLengthKey !== '2700') {
      add('decking_review', 'Decking quantity and price require review for the selected beam length.');
    }
    if (state.requestType === 'decking_only' && state.deckingOnly && state.deckingOnly.beamLengthKey !== '2700') {
      add('decking_review', 'Decking quantity and price require review for the selected beam length.');
    }

    var endBarrierSelected =
      (cr && (cr.endBarriers === 'single' || cr.endBarriers === 'double')) ||
      (state.accessoriesOnly && state.accessoriesOnly.category === 'barriers');
    if (endBarrierSelected) {
      add('end_barrier_review', 'End barriers requested. Price confirmed after review.');
    }

    var capacity = checkBeamCapacity(state, beamPriceResult);
    if (capacity) {
      add('beam_capacity',
        'That load may exceed this beam’s rated duty (' +
        capacity.swlKg + 'kg per level) — we’ll confirm the right beam on the call.');
    }

    return warnings;
  }

  function calculateGuideEstimate(state) {
    /* Multi-run: delegate entirely to the merge path and leave every
       line below untouched, so single-run/every-other-flow output is
       byte-identical by construction, not by care. */
    if (state.requestType === 'complete_run' && state.runs && state.runs.length >= 2) {
      return calculateMultiRunGuideEstimate(state);
    }

    var lineItems = [];
    var reviewItems = [];

    function addLine(key, label, quantity, unitPricePence, note) {
      if (quantity === null || quantity === undefined || quantity <= 0) return;
      if (unitPricePence === null || unitPricePence === undefined) {
        lineItems.push({ key: key, label: label, quantity: quantity, unitPricePence: null, totalPence: null, status: 'review' });
        reviewItems.push({ key: key, label: label, quantity: quantity, note: note || 'Price to confirm' });
      } else {
        lineItems.push({ key: key, label: label, quantity: quantity, unitPricePence: unitPricePence, totalPence: quantity * unitPricePence, status: 'priced' });
      }
    }

    var quantities = {};
    var framePriceResult = { unitPricePence: null };
    var beamPriceResult = { unitPricePence: null };

    if (state.requestType === 'complete_run') {
      var cr = state.completeRun;
      quantities = calculateCompleteRunQuantities(cr);

      framePriceResult = resolveFramePrice(state.brand, Number(cr.frameDepthMm), Number(cr.frameHeightMm));
      addLine('frames', 'Frames', quantities.frames, framePriceResult.unitPricePence, 'Frame price to confirm for this brand/size combination');

      beamPriceResult = resolveBeamPrice(state.brand, cr.beamLengthKey);
      addLine('beams', 'Beams', quantities.totalBeams, beamPriceResult.unitPricePence, 'Beam price to confirm for this brand/length combination');

      addLine('locking_pins', 'Locking pins', quantities.lockingPins, ACCESSORY_PRICES.lockingPinEach);

      if (cr.decking && cr.decking !== 'none' && cr.decking !== 'not_sure' && quantities.deckingPositions) {
        var deckingPriceResult = resolveDeckingPrice(cr.beamLengthKey, cr.decking, Number(cr.frameDepthMm));
        addLine('decking', 'Decking', quantities.deckingPositions, deckingPriceResult.pricePerPosition, 'Decking price to confirm for this beam length/depth');
      }

      addLine('floor_bolts', 'Floor fixings', quantities.floorBolts, ACCESSORY_PRICES.floorBoltEach);
      addLine('protectors', 'Frame leg protectors', quantities.protectors, ACCESSORY_PRICES.frameLegProtectorEach);
      addLine('load_notice', 'Load notice board', quantities.loadNotices, ACCESSORY_PRICES.loadNoticeBoardEach);

      if (quantities.endBarrierQty > 0) {
        addLine('end_barriers', 'End barriers', quantities.endBarrierQty, null, 'End barrier price confirmed after review');
      }
    } else if (state.requestType === 'frames_only') {
      var fo = state.framesOnly;
      quantities = calculateFramesOnlyQuantities(fo);
      framePriceResult = resolveFramePrice(state.brand, Number(fo.frameDepthMm), Number(fo.frameHeightMm));
      addLine('frames', 'Frames', quantities.frames, framePriceResult.unitPricePence, 'Frame price to confirm for this brand/size combination');
    } else if (state.requestType === 'beams_only') {
      var bo = state.beamsOnly;
      quantities = calculateBeamsOnlyQuantities(bo);
      beamPriceResult = resolveBeamPrice(state.brand, bo.beamLengthKey);
      addLine('beams', 'Beams', quantities.totalBeams, beamPriceResult.unitPricePence, 'Beam price to confirm for this brand/length combination');
      if (quantities.lockingPins !== null) {
        addLine('locking_pins', 'Locking pins', quantities.lockingPins, ACCESSORY_PRICES.lockingPinEach);
      }
    } else if (state.requestType === 'decking_only') {
      var dOnly = state.deckingOnly;
      quantities = calculateDeckingOnlyQuantities(dOnly);
      var deckingOnlyPriceResult = resolveDeckingPrice(dOnly.beamLengthKey, dOnly.type, Number(dOnly.depthMm));
      addLine('decking', 'Decking', quantities.deckingPositions, deckingOnlyPriceResult.pricePerPosition, 'Decking price to confirm for this beam length/depth');
    } else if (state.requestType === 'accessories_only') {
      var ao = state.accessoriesOnly;
      quantities = calculateAccessoriesOnlyQuantities(ao);
      addLine('floor_bolts', 'Floor fixings', quantities.floorBolts, ACCESSORY_PRICES.floorBoltEach);
      addLine('protectors', 'Frame leg protectors', quantities.protectors, ACCESSORY_PRICES.frameLegProtectorEach);
      addLine('load_notice', 'Load notice board', quantities.loadNotices, ACCESSORY_PRICES.loadNoticeBoardEach);
      addLine('locking_pins', 'Locking pins', quantities.lockingPins, ACCESSORY_PRICES.lockingPinEach);
      if (quantities.endBarrierQty > 0) {
        addLine('end_barriers', 'End barriers', quantities.endBarrierQty, null, 'End barrier price confirmed after review');
      }
    }

    var subtotalPence = lineItems.reduce(function (sum, li) {
      return li.status === 'priced' ? sum + li.totalPence : sum;
    }, 0);
    var vatPence = calcVat(subtotalPence);
    var totalIncVatPence = subtotalPence + vatPence;
    var isPartialEstimate = reviewItems.length > 0;

    var warnings = getWarnings(state, beamPriceResult);

    return {
      quantities: quantities, lineItems: lineItems, reviewItems: reviewItems, warnings: warnings,
      subtotalPence: subtotalPence, vatPence: vatPence, totalIncVatPence: totalIncVatPence,
      isPartialEstimate: isPartialEstimate
    };
  }

  /* ---- Multi-run merge (complete_run, runs.length >= 2 only) ----
     Reuses calculateGuideEstimate completely unmodified, once per
     run via a minimal shim state, then combines the results. Keeps
     every warning `key` unnamespaced (STAGES.onracking and
     ledgerHTML match on exact keys like 'beam_capacity' and
     'delivery_excluded') — per-run warnings are told apart by a
     `runIndex` tag and a "Run N: " text prefix instead; order-level
     warnings (brand/matching-existing based — identical on every
     run) collapse to one instance with runIndex null. */
  var PER_RUN_WARNING_KEYS = { mecalux_900: true, hilo_height: true, beam_2225_2250: true, decking_review: true, end_barrier_review: true, beam_capacity: true };

  function tagRunIndex(item, runIndex) {
    var out = {};
    for (var k in item) { if (item.hasOwnProperty(k)) out[k] = item[k]; }
    out.runIndex = runIndex;
    return out;
  }

  function calculateMultiRunGuideEstimate(state) {
    var lineItems = [];
    var reviewItems = [];
    var warnings = [];
    var runResults = [];

    state.runs.forEach(function (run, i) {
      var shimState = { requestType: 'complete_run', completeRun: run, brand: state.brand, matchingExisting: state.matchingExisting };
      var r = calculateGuideEstimate(shimState);
      var taggedLineItems = r.lineItems.map(function (li) { return tagRunIndex(li, i); });

      lineItems = lineItems.concat(taggedLineItems);
      reviewItems = reviewItems.concat(r.reviewItems.map(function (ri) { return tagRunIndex(ri, i); }));

      r.warnings.forEach(function (w) {
        var isPerRun = !!PER_RUN_WARNING_KEYS[w.key];
        var runIndex = isPerRun ? i : null;
        var exists = warnings.some(function (existing) { return existing.key === w.key && existing.runIndex === runIndex; });
        if (!exists) warnings.push({ key: w.key, text: isPerRun ? ('Run ' + (i + 1) + ': ' + w.text) : w.text, runIndex: runIndex });
      });

      runResults.push({
        index: i, label: 'Run ' + (i + 1), bays: run.bays,
        quantities: r.quantities, lineItems: taggedLineItems,
        subtotalPence: r.subtotalPence, isPartialEstimate: r.isPartialEstimate
      });
    });

    var subtotalPence = lineItems.reduce(function (sum, li) {
      return li.status === 'priced' ? sum + li.totalPence : sum;
    }, 0);
    var vatPence = calcVat(subtotalPence);
    var totalIncVatPence = subtotalPence + vatPence;
    var isPartialEstimate = reviewItems.length > 0;

    return {
      quantities: runResults[0].quantities, lineItems: lineItems, reviewItems: reviewItems, warnings: warnings,
      subtotalPence: subtotalPence, vatPence: vatPence, totalIncVatPence: totalIncVatPence,
      isPartialEstimate: isPartialEstimate, runs: runResults
    };
  }

  /* Decking-only: a factory (not just a literal) so "Deck another
     size" can rebuild a clean object without duplicating these
     defaults in two places. resultsShown/altExpanded/confirmedAdds
     are Phase 2 UI state, reset together whenever any Phase 1
     answer changes (see the stateSet hook below). */
  function defaultDeckingOnly() {
    return {
      type: 'mesh', depthMm: 900, beamLengthKey: '2700', beamLengthCustom: false,
      bays: 3, levels: 2, cartQty: {},
      resultsShown: false, altExpanded: false, confirmedAdds: {}
    };
  }

  function getDefaultState() {
    /* Multi-run: completeRun is always the SAME object reference as
       runs[activeRunIndex] — never a copy. stateGet/stateSet mutate
       state.completeRun in place, so keeping this identity in sync
       is what lets every existing per-run stage/field/drawing keep
       working unmodified, unaware runs even exist. */
    var cr = {
      bays: 3, frameHeightMm: 5000, frameDepthMm: 900, beamLengthKey: '2700', beamLevels: 2,
      onRacking: 'uk_standard', palletType: 'uk_standard', maxLoadPerLevelKg: 0, decking: 'none', floorBolts: 'no',
      frameProtectors: 'none', endBarriers: 'no', loadNotice: 'no',
      frameHeightCustom: false, beamLengthCustom: false, beamLevelsCustom: false
    };
    return {
      requestType: 'complete_run', matchingExisting: 'no', brand: 'not_sure',
      completeRun: cr,
      runs: [cr],
      activeRunIndex: 0,
      /* Reactive, not explicitly cleared everywhere: the banner only
         shows when runAddedBanner.newRun matches the CURRENTLY active
         run, so switching tabs away from the just-added run hides it
         automatically. Explicitly cleared on dismiss and on removing
         a run (see removeRun/click delegation). */
      runAddedBanner: null,
      framesOnly: { quantity: 2, frameHeightMm: 5000, frameDepthMm: 900, frameHeightCustom: false },
      beamsOnly: { beamLengthKey: '2700', beamLengthCustom: false, beamPairQuantity: 1, includeLockingPins: 'yes', maxLoadPerPalletKg: 0 },
      deckingOnly: defaultDeckingOnly(),
      accessoriesOnly: {
        category: 'floor_bolts', lockingPinsBrand: 'not_sure', cartQty: {},
        floorBoltFrames: 4, protectorFrames: 4, protectorCoverage: 'front',
        barrierRunEnds: 'one', pinsLevels: 4, loadNoticeQuantity: 0,
        resultsShown: false, altExpanded: false, confirmedAdds: {}
      },
      delivery: { requirement: 'supply_only', postcode: '', unloading: 'not_sure', timeframe: 'flexible' },
      customer: { name: '', company: '', email: '', phone: '', postcode: '', notes: '' },
      acknowledgedGuideEstimate: false
    };
  }

  function fmtQty(v) { return (v === null || v === undefined) ? 'Not sure / to confirm' : String(v); }

  function requestTypeLabel(key) {
    return {
      complete_run: 'Full racking bays', frames_only: 'Frames only', beams_only: 'Beams only',
      decking_only: 'Decking only', accessories_only: 'Accessories only', not_sure: 'Not sure / need help'
    }[key] || key;
  }

  function buildHumanReadablePayload(state, result) {
    var lines = [];
    function push(s) { lines.push(s == null ? '' : s); }

    push('USED PALLET RACKING QUOTE BUILDER REQUEST');
    push('');
    push('CUSTOMER');
    push('Name: ' + state.customer.name);
    push('Company: ' + state.customer.company);
    push('Email: ' + state.customer.email);
    push('Phone: ' + state.customer.phone);
    push('Postcode: ' + state.customer.postcode);
    push('');
    push('REQUEST TYPE');
    push(requestTypeLabel(state.requestType));
    push('');
    push('EXISTING RACKING');
    push('Matching existing: ' + state.matchingExisting);
    push('Brand/system: ' + (BRAND_LABELS[state.brand] || state.brand));
    if (state.brand === 'other' || state.brand === 'not_sure') {
      push('Guide-pricing basis: Link 51 (fallback — brand compatibility to confirm)');
    }

    if (state.requestType === 'complete_run' && state.runs.length >= 2) {
      state.runs.forEach(function (run, i) {
        var rq = result.runs[i].quantities;
        push('');
        push('RUN ' + (i + 1) + ' — ' + run.bays + ' bays · ' + (run.frameHeightMm / 1000) + 'm × ' + run.frameDepthMm + ' · ' + (BEAM_LENGTH_LABELS[run.beamLengthKey] || run.beamLengthKey) + ' beams · ' + run.beamLevels + ' levels');
        push('Bays: ' + run.bays);
        push('Calculated frames: ' + fmtQty(rq.frames));
        push('Frame height: ' + run.frameHeightMm + 'mm');
        push('Frame depth: ' + run.frameDepthMm + 'mm');
        push('Beam length: ' + (BEAM_LENGTH_LABELS[run.beamLengthKey] || run.beamLengthKey));
        push('Beam levels: ' + run.beamLevels);
        push('Beam pairs: ' + fmtQty(rq.beamPairs));
        push('Total beams: ' + fmtQty(rq.totalBeams));
        push('Locking pins included in request: ' + fmtQty(rq.lockingPins));
        push('Pallet type: ' + run.palletType);
        push('Approximate maximum load per beam level: ' + run.maxLoadPerLevelKg + 'kg');
        push('Decking type: ' + run.decking);
        push('Estimated decking positions: ' + fmtQty(rq.deckingPositions));
        push('Floor fixings requested: ' + run.floorBolts + ' (estimated ' + fmtQty(rq.floorBolts) + ')');
        push('Frame protectors: ' + run.frameProtectors + ' (estimated ' + fmtQty(rq.protectors) + ')');
        push('End barriers: ' + run.endBarriers);
        push('Load notice: ' + run.loadNotice);
      });
    } else if (state.requestType === 'complete_run') {
      var cr = state.completeRun;
      push('');
      push('RACKING SPECIFICATION');
      push('Bays: ' + cr.bays);
      push('Calculated frames: ' + fmtQty(result.quantities.frames));
      push('Frame height: ' + cr.frameHeightMm + 'mm');
      push('Frame depth: ' + cr.frameDepthMm + 'mm');
      push('Beam length: ' + (BEAM_LENGTH_LABELS[cr.beamLengthKey] || cr.beamLengthKey));
      push('Beam levels: ' + cr.beamLevels);
      push('Beam pairs: ' + fmtQty(result.quantities.beamPairs));
      push('Total beams: ' + fmtQty(result.quantities.totalBeams));
      push('Locking pins included in request: ' + fmtQty(result.quantities.lockingPins));
      push('Pallet type: ' + cr.palletType);
      push('Approximate maximum load per beam level: ' + cr.maxLoadPerLevelKg + 'kg');
      push('');
      push('DECKING');
      push('Type: ' + cr.decking);
      push('Estimated positions: ' + fmtQty(result.quantities.deckingPositions));
      push('');
      push('PROTECTION');
      push('Floor fixings requested: ' + cr.floorBolts);
      push('Estimated floor fixings: ' + fmtQty(result.quantities.floorBolts));
      push('Frame protectors: ' + cr.frameProtectors);
      push('Estimated protectors: ' + fmtQty(result.quantities.protectors));
      push('End barriers: ' + cr.endBarriers);
      push('Load notice: ' + cr.loadNotice);
    } else if (state.requestType === 'frames_only') {
      var fo = state.framesOnly;
      push('');
      push('FRAMES ONLY');
      push('Frame quantity: ' + fo.quantity);
      push('Frame height: ' + fo.frameHeightMm + 'mm');
      push('Frame depth: ' + fo.frameDepthMm + 'mm');
    } else if (state.requestType === 'beams_only') {
      var bo = state.beamsOnly;
      push('');
      push('BEAMS ONLY');
      push('Beam length: ' + (BEAM_LENGTH_LABELS[bo.beamLengthKey] || bo.beamLengthKey));
      push('Beam pairs requested: ' + bo.beamPairQuantity);
      push('Total beams: ' + fmtQty(result.quantities.totalBeams));
      push('Locking pins requested: ' + bo.includeLockingPins);
      push('Estimated locking pins: ' + fmtQty(result.quantities.lockingPins));
      push('Approximate maximum load per pallet: ' + bo.maxLoadPerPalletKg + 'kg');
    } else if (state.requestType === 'decking_only') {
      var dOnly = state.deckingOnly;
      push('');
      push('DECKING ONLY');
      push('Decking type: ' + dOnly.type);
      push('Deck depth: ' + dOnly.depthMm + 'mm');
      push('Beam/bay length: ' + (BEAM_LENGTH_LABELS[dOnly.beamLengthKey] || dOnly.beamLengthKey));
      push('Bays: ' + dOnly.bays);
      push('Levels to deck per bay: ' + dOnly.levels);
      push('Decking positions: ' + fmtQty(result.quantities.deckingPositions));
    } else if (state.requestType === 'accessories_only') {
      var ao = state.accessoriesOnly;
      push('');
      push('ACCESSORIES ONLY');
      push('Category: ' + ao.category);
      if (ao.category === 'floor_bolts') {
        push('Frames: ' + ao.floorBoltFrames);
        push('Floor bolts needed: ' + fmtQty(result.quantities.floorBolts));
      } else if (ao.category === 'leg_protectors') {
        push('Frames: ' + ao.protectorFrames);
        push('Coverage: ' + ao.protectorCoverage);
        push('Leg protectors needed: ' + fmtQty(result.quantities.protectors));
      } else if (ao.category === 'barriers') {
        push('Run ends to protect: ' + ao.barrierRunEnds);
        push('Barriers needed: ' + fmtQty(result.quantities.endBarrierQty));
      } else if (ao.category === 'locking_pins') {
        push('Locking pins brand: ' + ao.lockingPinsBrand);
        push('Beam levels: ' + ao.pinsLevels);
        push('Pins needed: ' + fmtQty(result.quantities.lockingPins));
      } else if (ao.category === 'load_notices') {
        push('Load notice boards: ' + ao.loadNoticeQuantity);
      }
    }

    push('');
    push('DELIVERY / INSTALLATION');
    push('Requirement: ' + state.delivery.requirement);
    push('Delivery postcode: ' + state.delivery.postcode);
    push('Unloading: ' + state.delivery.unloading);
    push('Preferred timeframe: ' + state.delivery.timeframe);

    push('');
    push('GUIDE ESTIMATE');
    result.lineItems.forEach(function (li) {
      push(li.label + ': ' + li.quantity + ' x ' + (li.status === 'priced' ? money(li.unitPricePence) : 'price to confirm') + (li.status === 'priced' ? ' = ' + money(li.totalPence) : ''));
    });
    push('');
    push((result.isPartialEstimate ? 'Guide total for priced items ex VAT' : 'Guide total ex VAT') + ': ' + money(result.subtotalPence));
    push('VAT: ' + money(result.vatPence));
    push((result.isPartialEstimate ? 'Guide total for priced items inc VAT' : 'Guide total inc VAT') + ': ' + money(result.totalIncVatPence));
    push('');
    push('Estimate completeness: ' + (result.isPartialEstimate ? 'Priced items only — some items require manual pricing' : 'Complete'));

    if (result.reviewItems.length) {
      push('');
      push('ITEMS TO CONFIRM');
      result.reviewItems.forEach(function (ri) {
        push('- ' + ri.label + ' (qty ' + ri.quantity + '): ' + ri.note);
      });
    }

    push('');
    push('WARNINGS');
    result.warnings.forEach(function (w) { push('- ' + w.text); });
    push('- Final stock, brand compatibility, load duty and installation requirements must be confirmed.');

    push('');
    push('CUSTOMER NOTES');
    push(state.customer.notes || '(none provided)');

    return lines.join('\n');
  }

  function buildJsonPayload(state, result, pageHandle) {
    var fullyPriced = result.subtotalPence > 0 && !result.isPartialEstimate;
    return {
      schemaVersion: 1,
      pricingVersion: '2026-08-v2',
      generatedAt: new Date().toISOString(),
      pageHandle: pageHandle || 'used-pallet-racking-quote-builder',
      requestType: state.requestType,
      state: state,
      quantities: result.quantities,
      lineItems: result.lineItems,
      warnings: result.warnings.map(function (w) { return w.text; }),
      reviewItems: result.reviewItems,
      runs: state.requestType === 'complete_run' ? state.runs : undefined,
      runCount: state.requestType === 'complete_run' ? state.runs.length : undefined,
      totals: { subtotalPence: result.subtotalPence, vatPence: result.vatPence, totalIncVatPence: result.totalIncVatPence },
      displayTotals: { subtotal: money(result.subtotalPence), vat: money(result.vatPence), totalIncVat: money(result.totalIncVatPence) },
      guidePriceDisplay: fullyPriced ? (money(result.subtotalPence) + ' ex VAT') : 'confirmed on your call',
      isPartialEstimate: result.isPartialEstimate
    };
  }

  /* ============================================================
     WIZARD — flows, rail, stage navigation
     ============================================================ */
  var FLOWS = {
    complete_run: ['need', 'size', 'onracking', 'delivery', 'send'],
    frames_only: ['need', 'size', 'delivery', 'send'],
    beams_only: ['need', 'size', 'delivery', 'send'],
    decking_only: ['need', 'size', 'delivery', 'send'],
    accessories_only: ['need', 'size', 'delivery', 'send'],
    not_sure: ['need', 'space', 'send']
  };
  var STAGE_NAMES = { need: 'Need', size: 'Size', onracking: 'On the racking', delivery: 'Delivery', send: 'Send', space: 'Your space' };
  var BEAM_MM = { 1200: 1200, 1815: 1815, '2225_2250': 2237, 2300: 2300, 2470: 2470, 2700: 2700, 3300: 3300, 3750: 3750 };
  var FIELD_PREFIX_MAP = { cr: 'completeRun', fo: 'framesOnly', bo: 'beamsOnly', do: 'deckingOnly', ao: 'accessoriesOnly' };
  /* Decking two-phase flow: any of these six fields changing kills
     whatever Phase 2 results are showing — see the stateSet hook. */
  var DECK_PHASE1_FIELDS = { type: true, depthMm: true, beamLengthKey: true, beamLengthCustom: true, bays: true, levels: true };
  /* Accessories two-phase flow: category and lockingPinsBrand MUST be
     included — _sizeAcc() renders the category chips unconditionally
     regardless of resultsShown, so a customer can pick a different
     category while Phase 2 is still showing without pressing "Add
     something else" first; without this the sheet would render a
     stale Phase 2 view for the new category. */
  var ACC_PHASE1_FIELDS = { category: true, lockingPinsBrand: true, floorBoltFrames: true, protectorFrames: true, protectorCoverage: true, barrierRunEnds: true, pinsLevels: true };
  var SHORT_HEIGHTS = [2000, 3000, 4000, 5000, 5500, 6000];
  function shortHeightOpts() { return SHORT_HEIGHTS.map(function (v) { return { v: v, l: (v / 1000) + 'm' }; }); }
  function shortBeamOpts() { return [{ v: '2225_2250', l: '2250' }, { v: '2300', l: '2300' }, { v: '2700', l: '2700' }, { v: '3300', l: '3300' }]; }

  /* ============================================================
     DOM / UI LAYER
     ============================================================ */
  var PENDING_KEY = 'rsi_uqb_pending_submission';
  var PENDING_MAX_AGE_MS = 5 * 60 * 1000;

  /* ---- Microsoft Ads (UET) conversion signal -------------------------------
     2026-10-05. UET tag 187275947 does not run in this page: Shopify loads it
     inside a web-pixel sandbox iframe, so window.uetq is undefined here and
     pushing to it from theme JS would reach nothing. The supported bridge is a
     Shopify analytics custom event, which the custom pixel "Microsoft Ads -
     RISI UET" subscribes to inside the sandbox and forwards to uetq. Consent is
     enforced by Shopify on the pixel side.

     Keyed on the exact form id; an unlisted id publishes nothing. The guard
     object is shared with assets/rsi-lead-confirm-modal.js and
     assets/rsi-lead-form-upload.js so one submission can never publish twice.
     ------------------------------------------------------------------------ */
  var LEAD_EVENTS = {
    UsedRackingQuoteBuilder: 'risi_quote_builder_submitted'
  };

  window.__rsiLeadUetFired = window.__rsiLeadUetFired || {};

  function fireLeadConversion(formId, source) {
    var eventName = LEAD_EVENTS[formId];
    if (!eventName) return;
    if (window.__rsiLeadUetFired[eventName]) return;
    window.__rsiLeadUetFired[eventName] = true;
    try {
      window.Shopify.analytics.publish(eventName, { form_id: formId, source: source });
    } catch (e) { /* analytics unavailable — tracking is never allowed to break the form */ }
  }

  var SKINS = window.RSI_UQB_V2_SKINS || {};
  function IM(k, x, y, w, h, op) {
    if (!SKINS[k]) return '';
    return '<image href="' + SKINS[k] + '" x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" preserveAspectRatio="none"' + (op ? ' opacity="' + op + '"' : '') + '/>';
  }

  function initializeBuilder(root) {
    var state = getDefaultState();
    var pageHandle = root.getAttribute('data-rsi-uqb-page-handle') || 'used-pallet-racking-quote-builder';
    var stageIx = 0;

    var form = root.querySelector('[data-rsi-uqb-form]');
    /* The real <form> is form's ANCESTOR (form wraps this div, not the
       reverse) — needed so the mbar's mirrored Send button, which
       lives outside the <form>, can reference it via form="id". */
    var formEl = form ? form.closest('form') : null;
    var stagePane = root.querySelector('[data-rsi-uqb-v2-stagepane]');
    var stageOuter = root.querySelector('.rsi-uqb-v2__stage');
    var railHost = root.querySelector('[data-rsi-uqb-v2-rail]');
    var summaryDesktop = root.querySelector('[data-rsi-uqb-summary-desktop]');
    var summaryMobile = root.querySelector('[data-rsi-uqb-summary-mobile]');
    var drawDesktop = root.querySelector('[data-rsi-uqb-v2-draw-desktop]');
    var drawMobile = root.querySelector('[data-rsi-uqb-v2-draw-mobile]');
    var payloadField = root.querySelector('[data-rsi-uqb-payload-field]');
    var bodyField = root.querySelector('[data-rsi-uqb-body-field]');
    var mobileBar = root.querySelector('[data-rsi-uqb-mobile-bar]');
    var mbarCtaHost = root.querySelector('[data-rsi-uqb-v2-mbar-cta]');
    var errorSummary = root.querySelector('[data-rsi-uqb-error-summary]');
    var successPanel = root.querySelector('[data-rsi-uqb-success]');
    var interactionStarted = false;

    /* ---- Decking-only direct-purchase catalogue: a static JSON asset
       (see scripts/regenerate-deck-catalogue.js), fetched once at init
       and re-rendered into view only if the visitor is on/reaches the
       decking_only flow — never blocks any other flow. ---- */
    var deckCatalogue = null;
    var deckCatalogueLoading = true;
    var deckCatalogueUrl = root.getAttribute('data-rsi-uqb-v2-deck-catalogue-url');
    if (deckCatalogueUrl) {
      fetch(deckCatalogueUrl).then(function (r) { return r.json(); }).then(function (data) {
        deckCatalogue = data.products || [];
        deckCatalogueLoading = false;
        if (state.requestType === 'decking_only') render();
      }).catch(function () {
        deckCatalogue = [];
        deckCatalogueLoading = false;
        if (state.requestType === 'decking_only') render();
      });
    } else {
      deckCatalogueLoading = false;
      deckCatalogue = [];
    }

    /* ---- Accessories & safety catalogue — same pattern as the deck
       catalogue above, own JSON asset, own regeneration step (see
       scripts/regenerate-accessories-catalogue.js). ---- */
    var accessoriesCatalogue = null;
    var accessoriesCatalogueLoading = true;
    var accessoriesCatalogueUrl = root.getAttribute('data-rsi-uqb-v2-accessories-catalogue-url');
    if (accessoriesCatalogueUrl) {
      fetch(accessoriesCatalogueUrl).then(function (r) { return r.json(); }).then(function (data) {
        accessoriesCatalogue = data.products || [];
        accessoriesCatalogueLoading = false;
        if (state.requestType === 'accessories_only') render();
      }).catch(function () {
        accessoriesCatalogue = [];
        accessoriesCatalogueLoading = false;
        if (state.requestType === 'accessories_only') render();
      });
    } else {
      accessoriesCatalogueLoading = false;
      accessoriesCatalogue = [];
    }

    /* ---- Real, site-wide Shopify cart -- not a locally-tracked "what
       this session added" tally. The basket bar/bottom bar must agree
       with the header cart icon and /cart, so they all read this same
       source, re-fetched fresh after every add. ---- */
    var realCart = null;
    function refreshRealCart(cb) {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        realCart = cart;
        if (cb) cb();
      }).catch(function () { if (cb) cb(); });
    }
    refreshRealCart(function () { if (state.requestType === 'decking_only' || state.requestType === 'accessories_only') render(); });

    /* ---- state path get/set: "cr.bays" <-> state.completeRun.bays ---- */
    function stateGet(path) {
      var parts = path.split('.');
      if (parts.length === 1) return state[parts[0]];
      var branch = FIELD_PREFIX_MAP[parts[0]] || parts[0];
      return state[branch] ? state[branch][parts[1]] : undefined;
    }
    function stateSet(path, value) {
      var parts = path.split('.');
      if (parts.length === 1) { state[parts[0]] = value; return; }
      var branch = FIELD_PREFIX_MAP[parts[0]] || parts[0];
      if (!state[branch]) return;
      state[branch][parts[1]] = value;
      /* Any Phase 1 decking answer changing kills Phase 2 — no silent
         reshuffles ever. Single choke point so it fires regardless of
         which UI control (chip, stepper, custom-width input) wrote it. */
      if (branch === 'deckingOnly' && DECK_PHASE1_FIELDS[parts[1]] && state.deckingOnly.resultsShown) {
        state.deckingOnly.resultsShown = false;
        state.deckingOnly.altExpanded = false;
        state.deckingOnly.confirmedAdds = {};
      }
      if (branch === 'accessoriesOnly' && ACC_PHASE1_FIELDS[parts[1]] && state.accessoriesOnly.resultsShown) {
        state.accessoriesOnly.resultsShown = false;
        state.accessoriesOnly.altExpanded = false;
        state.accessoriesOnly.confirmedAdds = {};
      }
    }

    function flow() { return FLOWS[state.requestType] || FLOWS.complete_run; }

    /* Cart-quantity store for whichever direct-purchase flow is
       currently active — decking and accessories each keep their own
       object (state.deckingOnly.cartQty / state.accessoriesOnly.cartQty)
       but every add/stepper/input handler is catalogue-agnostic, so
       they all go through this one lookup rather than hardcoding
       which flow's object to touch. */
    function activeCartQty() {
      return state.requestType === 'accessories_only' ? state.accessoriesOnly.cartQty : state.deckingOnly.cartQty;
    }

    /* ============================================================
       STAGE HTML TEMPLATES — only the CURRENT stage's markup is ever
       in the DOM; every input carries the real data-rsi-uqb-field
       path phase-1 expects, populated from `state` at render time.
       ============================================================ */
    function esc(v) {
      return (v === undefined || v === null) ? '' : String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function chipsHTML(path, opts, extraClass) {
      var cur = String(stateGet(path));
      var name = 'rsi_uqb_v2_' + path.replace(/\./g, '_');
      return '<div class="rsi-uqb-v2__chips" role="radiogroup" aria-label="' + esc(opts.label || '') + '">' + opts.map(function (o) {
        var checked = String(o.v) === cur ? ' checked' : '';
        return '<label class="rsi-uqb-v2__chip' + (extraClass ? ' ' + extraClass : '') + '"><input class="rsi-uqb-v2__native" type="radio" name="' + name + '" data-rsi-uqb-field="' + path + '" value="' + esc(o.v) + '"' + checked + '>' + esc(o.l) + '</label>';
      }).join('') + '</div>';
    }

    /* Chip group + trailing "Custom" chip that reveals an inline
       numeric input. customFlagPath is a sibling boolean state path
       (e.g. "cr.frameHeightCustom") — the raw value path keeps
       holding the real number throughout, custom or not, so pricing/
       drawing/payload never need to know which mode produced it. */
    function customChipsHTML(path, customFlagPath, opts, inputOpts) {
      var customOn = !!stateGet(customFlagPath);
      var cur = String(stateGet(path));
      var name = 'rsi_uqb_v2_' + path.replace(/\./g, '_');
      var h = '<div class="rsi-uqb-v2__chips" role="radiogroup">' + opts.map(function (o) {
        var checked = (!customOn && String(o.v) === cur) ? ' checked' : '';
        return '<label class="rsi-uqb-v2__chip"><input class="rsi-uqb-v2__native" type="radio" name="' + name + '" data-rsi-uqb-field="' + path + '" data-rsi-uqb-v2-clear-custom="' + customFlagPath + '" value="' + esc(o.v) + '"' + checked + '>' + esc(o.l) + '</label>';
      }).join('') +
        '<label class="rsi-uqb-v2__chip"><input class="rsi-uqb-v2__native" type="radio" name="' + name + '" data-rsi-uqb-field="' + path + '" data-rsi-uqb-v2-custom-toggle="' + customFlagPath + '" value="__custom__"' + (customOn ? ' checked' : '') + '>Custom</label>' +
        '</div>';
      if (customOn) {
        var v = stateGet(path);
        h += '<div class="rsi-uqb-v2__custom-num"><label class="rsi-uqb-v2__f-label" for="' + inputOpts.id + '">' + inputOpts.label + '</label>' +
          '<input class="rsi-uqb-v2__tin rsi-uqb-v2__tin--custom" type="text" inputmode="numeric" id="' + inputOpts.id + '" data-rsi-uqb-field="' + path + '" data-rsi-uqb-v2-custom-numeric value="' + ((v || v === 0) ? esc(String(v)) : '') + '" placeholder="' + esc(inputOpts.placeholder || '') + '">' +
          (inputOpts.hint ? '<p class="rsi-uqb-v2__hint">' + esc(inputOpts.hint) + '</p>' : '') +
          '</div>';
      }
      return h;
    }

    function cardsHTML(path, opts) {
      var cur = String(stateGet(path));
      var name = 'rsi_uqb_v2_' + path.replace(/\./g, '_');
      return '<div class="rsi-uqb-v2__cards" role="radiogroup" aria-label="What do you need?">' + opts.map(function (o) {
        var checked = String(o.v) === cur ? ' checked' : '';
        return '<label class="rsi-uqb-v2__card"><input class="rsi-uqb-v2__native" type="radio" name="' + name + '" data-rsi-uqb-field="' + path + '" value="' + esc(o.v) + '"' + checked + '>' + o.icon + '<b>' + esc(o.b) + '</b><span>' + esc(o.s) + '</span></label>';
      }).join('') + '</div>';
    }

    function stepperHTML(path, min, max, id, label) {
      var v = stateGet(path);
      return '<label class="rsi-uqb-v2__f-label" for="' + id + '">' + label + '</label>' +
        '<div class="rsi-uqb-v2__stepper" data-rsi-uqb-stepper>' +
        '<button type="button" data-rsi-uqb-step-down aria-label="Decrease">&minus;</button>' +
        '<input inputmode="numeric" id="' + id + '" value="' + esc(v) + '" min="' + min + '" max="' + max + '" data-rsi-uqb-field="' + path + '">' +
        '<button type="button" data-rsi-uqb-step-up aria-label="Increase">+</button></div>';
    }

    function textHTML(path, opts) {
      opts = opts || {};
      var v = stateGet(path) || '';
      return '<input class="rsi-uqb-v2__tin' + (opts.cls ? ' ' + opts.cls : '') + '"' +
        (opts.style ? ' style="' + opts.style + '"' : '') +
        ' type="' + (opts.type || 'text') + '"' +
        (opts.id ? ' id="' + opts.id + '"' : '') +
        (opts.name ? ' name="' + opts.name + '"' : '') +
        (opts.required ? ' required' : '') +
        (opts.autocomplete ? ' autocomplete="' + opts.autocomplete + '"' : '') +
        ' data-rsi-uqb-field="' + path + '"' +
        (opts.placeholder ? ' placeholder="' + esc(opts.placeholder) + '"' : '') +
        ' value="' + esc(v) + '">';
    }

    var CARD_ICONS = window.RSI_UQB_V2_CARD_ICONS || {};
    var HEROES = window.RSI_UQB_V2_HEROES || {};
    function cardImg(key) {
      return CARD_ICONS[key]
        ? '<img class="rsi-uqb-v2__card-icon" src="' + CARD_ICONS[key] + '" alt="" width="46" height="34" loading="lazy">'
        : '';
    }
    var GLYPHS = {
      run: cardImg('completeRun'),
      frame: cardImg('framesOnly'),
      beam: cardImg('beamsOnly'),
      deck: cardImg('deckingOnly'),
      acc: cardImg('accessoriesOnly'),
      help: '<svg class="rsi-uqb-v2__card-icon" viewBox="0 0 46 34" aria-hidden="true"><rect x="6" y="4" width="34" height="26" rx="3" fill="none" stroke="#67718C" stroke-width="2" stroke-dasharray="4 3"/><text x="23" y="22" text-anchor="middle" font-size="13" fill="#67718C">?</text></svg>'
    };

    /* ---- Decking-only fit rule + catalogue matching ----
       decks per bay = largest n where n * deckWidth <= bayWidth AND
       the leftover coverage gap is <= 130mm. Since gap only grows as
       n shrinks, the largest valid n always gives the smallest gap —
       so if THAT gap already exceeds 130mm, no n fits and this
       product is excluded, not retried at a smaller n. */
    function deckBayWidthMm(beamLengthKey) {
      if (beamLengthKey === '2225_2250') return 2250; // the literal "2250" bay width, not the 2237 drawing approximation
      var mm = BEAM_MM[beamLengthKey];
      if (mm !== undefined) return mm;
      return Number(beamLengthKey) || 0;
    }
    function isDeckingFallback(d) {
      return d.beamLengthKey === 'not_sure' || !!d.beamLengthCustom;
    }
    function matchDeckProducts(d) {
      if (!deckCatalogue || !deckCatalogue.length) return [];
      var actualDepth = d.type === 'mesh' ? (Number(d.depthMm) === 900 ? 910 : 1110) : Number(d.depthMm);
      var bayWidthMm = deckBayWidthMm(d.beamLengthKey);
      if (!bayWidthMm) return [];
      var bays = Number(d.bays) || 0;
      var levels = Number(d.levels) || 0;
      var results = [];
      deckCatalogue.forEach(function (p) {
        if (p.type !== d.type || p.depthMm !== actualDepth || !p.available) return;
        var n = Math.floor(bayWidthMm / p.widthMm);
        if (n < 1) return;
        var gap = bayWidthMm - n * p.widthMm;
        if (gap > 130) return;
        results.push({ product: p, perLevel: n, gap: gap, suggestedQty: n * bays * levels });
      });
      results.sort(function (a, b) { return a.gap - b.gap || a.product.priceGBP - b.product.priceGBP; });
      return results;
    }

    /* ---- Accessories & safety: one category at a time, matched
       against the accessories catalogue. Locking pins additionally
       filter by brand — "not sure" (or a brand the store doesn't
       stock) naturally yields zero matches, same fallback trigger as
       every other empty category, no separate flag needed. ---- */
    var ACCESSORY_CATEGORY_LABELS = { floor_bolts: 'Floor bolts', leg_protectors: 'Leg protectors', barriers: 'Barriers', load_notices: 'Load notices', locking_pins: 'Locking pins' };
    var ACCESSORY_FALLBACK_NOTES = {
      floor_bolts: 'We’ll quote this for you on the call.',
      leg_protectors: 'We’ll quote this for you on the call.',
      barriers: 'We’ll quote this for you on the call.',
      load_notices: 'Load notices are made to your run’s spec — we’ll sort it on the call.',
      locking_pins: 'Pins are brand-specific — we’ll match yours on the call.'
    };
    /* floor_bolts/leg_protectors/barriers no longer have a raw
       editable fallback quantity — their Phase 1 answer (frames/
       coverage/run-ends) is already collected before ever reaching
       fallback, so those categories' fallback view is INFO-note-only,
       same as decking's. Only load_notices (no catalogue products,
       no Phase 1 question) still needs its own stepper here. */
    var ACCESSORY_FALLBACK_FIELD = { load_notices: 'ao.loadNoticeQuantity' };
    function matchAccessoryProducts(ao) {
      if (!accessoriesCatalogue || !accessoriesCatalogue.length) return [];
      if (ao.category === 'locking_pins') {
        if (ao.lockingPinsBrand === 'not_sure') return [];
        return accessoriesCatalogue.filter(function (p) { return p.type === 'locking_pins' && p.brand === ao.lockingPinsBrand && p.available; });
      }
      return accessoriesCatalogue.filter(function (p) { return p.type === ao.category && p.available; });
    }

    /* ---- Decking-only cart: same-origin AJAX cart, independent of the
       quote form entirely -- never touches contact[...] fields, never
       fires the n8n webhook (that only happens in the quote submit
       handler further down, a completely separate code path). ---- */
    var justAddedVariantId = null;
    /* One-shot: set true by addRun(), read and cleared the very next
       time runTabsHTML() renders (inside the same render() call
       addRun()'s goToStage triggers) — guarantees the entrance
       animation plays exactly once per add, not on every keystroke
       while the tabs remain on screen. */
    var runTabsEnterPending = false;
    function addProductToCart(variantId, qty, cardEl) {
      var addBtn = cardEl.querySelector('[data-rsi-uqb-v2-deck-add]');
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Adding…'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }], sections: 'cart-icon-bubble' })
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error((err && err.description) || 'Add to basket failed'); });
        return res.json();
      }).then(function (addData) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && addData.sections && addData.sections['cart-icon-bubble']) {
          bubble.innerHTML = addData.sections['cart-icon-bubble'];
        }
        justAddedVariantId = variantId;
        if (state.requestType === 'decking_only') state.deckingOnly.confirmedAdds[variantId] = qty;
        else if (state.requestType === 'accessories_only') state.accessoriesOnly.confirmedAdds[variantId] = qty;
        refreshRealCart(function () {
          refreshSheet();
          setTimeout(function () {
            if (justAddedVariantId === variantId) { justAddedVariantId = null; refreshSheet(); }
          }, 1600);
        });
      }).catch(function () {
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Try again';
          setTimeout(function () { if (addBtn) addBtn.textContent = 'Add to basket'; }, 2200);
        }
      });
    }

    function deckMoney(gbp) {
      return CURRENCY_FORMATTER ? CURRENCY_FORMATTER.format(gbp) : ('£' + gbp.toFixed(2));
    }

    /* Real commerce, not a guide estimate — every price here is what
       you'll actually pay, so no "guide"/"on the call" language. Reads
       the REAL Shopify cart (realCart, see refreshRealCart above),
       filtered to just the products the given catalogue sells — never
       a locally-tracked "what this session added" tally, so it agrees
       with the header cart icon and /cart regardless of what else is
       in the visitor's basket or what they added in an earlier visit.
       Shared core for both direct-purchase flows (decking, accessories
       — each reads its own catalogue, never double-counts the other's
       items even if both sit in the same real cart at once). */
    function catalogueCartSummary(catalogue) {
      if (!realCart || !realCart.items || !catalogue) return { count: 0, totalGBP: 0 };
      var ids = {};
      catalogue.forEach(function (p) { ids[p.variantId] = true; });
      var count = 0, totalPence = 0;
      realCart.items.forEach(function (item) {
        if (ids[item.variant_id]) { count += item.quantity; totalPence += item.line_price; }
      });
      return { count: count, totalGBP: totalPence / 100 };
    }
    function deckCartSummary() { return catalogueCartSummary(deckCatalogue); }
    function accessoryCartSummary() { return catalogueCartSummary(accessoriesCatalogue); }

    function productBasketBarHTML(summary, emptyLabel, singularLabel, pluralLabel) {
      var h = '<div class="rsi-uqb-v2__deck-basketbar">';
      if (!summary.count) {
        h += '<p class="rsi-uqb-v2__deck-basketbar-empty">' + emptyLabel + '</p>';
      } else {
        h += '<p class="rsi-uqb-v2__deck-basketbar-summary">' + summary.count + ' ' + (summary.count === 1 ? singularLabel : pluralLabel) + ' in your basket — ' + deckMoney(summary.totalGBP) + '</p>' +
          '<div class="rsi-uqb-v2__deck-basketbar-actions">' +
          '<a class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go" href="/cart">View basket</a>' +
          '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--back" data-rsi-uqb-v2-deck-keep-adding>Keep adding</button>' +
          '</div>';
      }
      h += '<p class="rsi-uqb-v2__deck-trade-link"><a href="#" data-rsi-uqb-v2-trade-link>Buying 50+? Get trade pricing instead</a></p>';
      h += '</div>';
      return h;
    }
    function deckBasketBarHTML() { return productBasketBarHTML(deckCartSummary(), 'Your basket is empty — add a deck to get started', 'deck', 'decks'); }
    function accessoryBasketBarHTML() { return productBasketBarHTML(accessoryCartSummary(), 'Your basket is empty — add an accessory to get started', 'item', 'items'); }

    var DECK_FINISH_SHORT = { 'Powder Coated': 'Powder coated', 'Bright Zinc Plated': 'Bright zinc' };
    function deckCardName(p) {
      if (p.type === 'mesh') return (DECK_FINISH_SHORT[p.finish] || p.finish) + ' mesh deck';
      if (p.type === 'timber') return 'Slatted timber deck';
      if (p.type === 'chipboard') return '22mm chipboard deck';
      return p.finish + ' ' + p.type + ' deck';
    }

    /* Persistent (not auto-reverting) confirmation once a decking or
       accessories product is actually added — deliberately separate
       from the shared justAddedVariantId/1600ms transient swap.
       Cleared by the stateSet Phase-1 hook or the flow's own reset
       button (resetAttr/resetLabel). Shared between both flows so
       wording/markup can't drift between two near-duplicate copies. */
    function basketConfirmBlockHTML(qty, priceGBP, singularNoun, resetAttr, resetLabel) {
      return '<div class="rsi-uqb-v2__deck-confirm">' +
        '<p class="rsi-uqb-v2__deck-confirm-msg">&#10003; ' + qty + ' ' + (qty === 1 ? singularNoun : singularNoun + 's') + ' in your basket — ' + deckMoney(qty * priceGBP) + '</p>' +
        '<div class="rsi-uqb-v2__deck-confirm-actions">' +
        '<a class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go" href="/cart">View basket</a>' +
        '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--back" ' + resetAttr + '>' + resetLabel + '</button>' +
        '</div></div>';
    }

    function deckCardsHTML(matches, d) {
      var h = '<div class="rsi-uqb-v2__deck-cards">';
      matches.forEach(function (m) {
        var p = m.product;
        if (d.cartQty[p.variantId] === undefined) d.cartQty[p.variantId] = m.suggestedQty;
        var qty = d.cartQty[p.variantId];
        var metaLine = p.depthMm + ' × ' + p.widthMm + 'mm' + (p.udlKg ? ' · ' + p.udlKg + 'kg UDL' : '');
        var justAdded = p.variantId === justAddedVariantId;
        var confirmedQty = d.confirmedAdds[p.variantId];
        h += '<div class="rsi-uqb-v2__deck-card">' +
          '<a class="rsi-uqb-v2__deck-card-link" href="/products/' + esc(p.handle) + '" title="' + esc(p.title) + '">' +
          '<img class="rsi-uqb-v2__deck-card-img" src="' + esc(p.image || '') + '" alt="' + esc(p.title) + '" loading="lazy">' +
          '<b class="rsi-uqb-v2__deck-card-name">' + esc(deckCardName(p)) + '</b>' +
          '</a>' +
          '<span class="rsi-uqb-v2__deck-card-dims">' + esc(metaLine) + '</span>' +
          '<span class="rsi-uqb-v2__deck-card-fit">' + m.perLevel + ' per level · ' + m.gap + 'mm gap</span>';
        if (confirmedQty !== undefined) {
          h += basketConfirmBlockHTML(confirmedQty, p.priceGBP, 'deck', 'data-rsi-uqb-v2-deck-another', 'Deck another size');
        } else {
          h += '<div class="rsi-uqb-v2__deck-card-row">' +
            '<span class="rsi-uqb-v2__deck-card-price"><b>' + deckMoney(p.priceGBP) + '</b> each</span>' +
            '<div class="rsi-uqb-v2__stepper rsi-uqb-v2__stepper--sm" data-rsi-uqb-stepper>' +
            '<button type="button" data-rsi-uqb-step-down aria-label="Decrease">&minus;</button>' +
            '<input inputmode="numeric" value="' + esc(qty) + '" min="0" max="999" data-rsi-uqb-v2-deck-qty="' + p.variantId + '">' +
            '<button type="button" data-rsi-uqb-step-up aria-label="Increase">+</button></div></div>' +
            '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__deck-add' + (justAdded ? ' rsi-uqb-v2__deck-add--added' : '') + '" data-rsi-uqb-v2-deck-add="' + p.variantId + '">' + (justAdded ? 'Added ✓' : 'Add to basket') + '</button>';
        }
        h += '</div>';
      });
      h += '</div>';
      return h;
    }

    /* Best-match card: same body as a compact card, badged, with a
       live-computed CTA ("Add all N to basket — £X") that recomputes
       on every render — no extra event wiring needed, the stepper
       click already triggers a full render(). */
    function deckBestMatchCardHTML(m, d) {
      var p = m.product;
      if (d.cartQty[p.variantId] === undefined) d.cartQty[p.variantId] = m.suggestedQty;
      var qty = d.cartQty[p.variantId];
      var metaLine = p.depthMm + ' × ' + p.widthMm + 'mm' + (p.udlKg ? ' · ' + p.udlKg + 'kg UDL' : '');
      var confirmedQty = d.confirmedAdds[p.variantId];
      var h = '<div class="rsi-uqb-v2__deck-card rsi-uqb-v2__deck-card--best">' +
        '<span class="rsi-uqb-v2__deck-card-badge">Best match</span>' +
        '<a class="rsi-uqb-v2__deck-card-link" href="/products/' + esc(p.handle) + '" title="' + esc(p.title) + '">' +
        '<img class="rsi-uqb-v2__deck-card-img" src="' + esc(p.image || '') + '" alt="' + esc(p.title) + '" loading="lazy">' +
        '<b class="rsi-uqb-v2__deck-card-name">' + esc(deckCardName(p)) + '</b>' +
        '</a>' +
        '<span class="rsi-uqb-v2__deck-card-dims">' + esc(metaLine) + '</span>' +
        '<span class="rsi-uqb-v2__deck-card-fit">' + m.perLevel + ' per level · ' + m.gap + 'mm gap</span>';
      if (confirmedQty !== undefined) {
        h += basketConfirmBlockHTML(confirmedQty, p.priceGBP, 'deck', 'data-rsi-uqb-v2-deck-another', 'Deck another size');
      } else {
        h += '<div class="rsi-uqb-v2__deck-card-row">' +
          '<span class="rsi-uqb-v2__deck-card-price"><b>' + deckMoney(p.priceGBP) + '</b> each</span>' +
          '<div class="rsi-uqb-v2__stepper rsi-uqb-v2__stepper--sm" data-rsi-uqb-stepper>' +
          '<button type="button" data-rsi-uqb-step-down aria-label="Decrease">&minus;</button>' +
          '<input inputmode="numeric" value="' + esc(qty) + '" min="0" max="999" data-rsi-uqb-v2-deck-qty="' + p.variantId + '">' +
          '<button type="button" data-rsi-uqb-step-up aria-label="Increase">+</button></div></div>' +
          '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__deck-add rsi-uqb-v2__deck-add--best" data-rsi-uqb-v2-deck-add="' + p.variantId + '">Add all ' + qty + ' to basket — ' + deckMoney(qty * p.priceGBP) + '</button>';
      }
      h += '</div>';
      return h;
    }

    /* Phase 2 results view: the working (maths shown, not implied),
       the best match, then alternatives collapsed behind a toggle. */
    function deckResultsHTML(matches, d) {
      var best = matches[0];
      var alts = matches.slice(1);
      var bays = Number(d.bays) || 0, levels = Number(d.levels) || 0;
      var bayWidthMm = deckBayWidthMm(d.beamLengthKey);
      var h = '<div class="rsi-uqb-v2__deck-results">';
      h += '<p class="rsi-uqb-v2__deck-working">You need <b>' + best.suggestedQty + ' decks</b> — ' + bays + ' bays × ' + levels + ' levels, ' + best.perLevel + ' decks per level on a ' + bayWidthMm + 'mm bay (' + best.gap + 'mm gap).</p>';
      h += deckBestMatchCardHTML(best, d);
      if (alts.length) {
        h += '<button type="button" class="rsi-uqb-v2__deck-alt-toggle" data-rsi-uqb-v2-deck-alt-toggle aria-expanded="' + (d.altExpanded ? 'true' : 'false') + '">Other options — heavier duty &amp; finishes (' + alts.length + ')<span class="rsi-uqb-v2__deck-alt-chevron">' + (d.altExpanded ? '&#9650;' : '&#9660;') + '</span></button>';
        h += '<div class="rsi-uqb-v2__deck-alts"' + (d.altExpanded ? '' : ' hidden') + '>' + deckCardsHTML(alts, d) + '</div>';
      }
      h += '</div>';
      return h;
    }

    /* Accessories & safety cards reuse every deck-card CSS class for
       visual consistency, but skip the fit-note line entirely — "N
       per level · Xmm gap" is a decking-specific bay-fit concept that
       doesn't apply to a floor bolt or a locking pin. */
    function accessoryCardName(p) {
      if (p.type === 'floor_bolts') return 'Floor bolt';
      if (p.type === 'leg_protectors') return (p.brand ? p.brand + ' leg protector' : 'Leg protector');
      if (p.type === 'barriers') return (p.brand ? p.brand + ' end barrier' : 'End barrier');
      if (p.type === 'locking_pins') return p.brand + ' locking pin';
      return p.title;
    }
    function accessoryMetaLine(p) {
      var bits = [];
      if (p.lengthMm) bits.push(p.lengthMm + 'mm');
      if (p.thread) bits.push(p.thread);
      if (p.steelGaugeMm) bits.push(p.steelGaugeMm + 'mm steel');
      if (p.dims) bits.push(p.dims);
      bits.push(p.condition === 'used' ? 'Used' : 'New');
      return bits.join(' · ');
    }
    function accessoryCardsHTML(matches, ao, qtyDefault) {
      var h = '<div class="rsi-uqb-v2__deck-cards">';
      matches.forEach(function (p) {
        if (ao.cartQty[p.variantId] === undefined) ao.cartQty[p.variantId] = qtyDefault;
        var qty = ao.cartQty[p.variantId];
        var justAdded = p.variantId === justAddedVariantId;
        var confirmedQty = ao.confirmedAdds[p.variantId];
        h += '<div class="rsi-uqb-v2__deck-card">' +
          '<a class="rsi-uqb-v2__deck-card-link" href="/products/' + esc(p.handle) + '" title="' + esc(p.title) + '">' +
          '<img class="rsi-uqb-v2__deck-card-img" src="' + esc(p.image || '') + '" alt="' + esc(p.title) + '" loading="lazy">' +
          '<b class="rsi-uqb-v2__deck-card-name">' + esc(accessoryCardName(p)) + '</b>' +
          '</a>' +
          '<span class="rsi-uqb-v2__deck-card-dims">' + esc(accessoryMetaLine(p)) + '</span>';
        if (confirmedQty !== undefined) {
          h += basketConfirmBlockHTML(confirmedQty, p.priceGBP, ACCESSORY_NOUNS[ao.category] || 'item', 'data-rsi-uqb-v2-acc-add-else', 'Add something else');
        } else {
          h += '<div class="rsi-uqb-v2__deck-card-row">' +
            '<span class="rsi-uqb-v2__deck-card-price"><b>' + deckMoney(p.priceGBP) + '</b> each</span>' +
            '<div class="rsi-uqb-v2__stepper rsi-uqb-v2__stepper--sm" data-rsi-uqb-stepper>' +
            '<button type="button" data-rsi-uqb-step-down aria-label="Decrease">&minus;</button>' +
            '<input inputmode="numeric" value="' + esc(qty) + '" min="0" max="999" data-rsi-uqb-v2-deck-qty="' + p.variantId + '">' +
            '<button type="button" data-rsi-uqb-step-up aria-label="Increase">+</button></div></div>' +
            '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__deck-add' + (justAdded ? ' rsi-uqb-v2__deck-add--added' : '') + '" data-rsi-uqb-v2-deck-add="' + p.variantId + '">' + (justAdded ? 'Added ✓' : 'Add to basket') + '</button>';
        }
        h += '</div>';
      });
      h += '</div>';
      return h;
    }

    /* Accessories two-phase flow: single computed "need" quantity per
       category, shared by the best match and every alternative alike
       (unlike decking, where quantity varies per product by width). */
    function accessoryComputedQty(ao) {
      if (ao.category === 'floor_bolts') return (Number(ao.floorBoltFrames) || 0) * 4;
      if (ao.category === 'leg_protectors') { var f = Number(ao.protectorFrames) || 0; return ao.protectorCoverage === 'all' ? f * 2 : f; }
      if (ao.category === 'barriers') return ao.barrierRunEnds === 'both' ? 2 : 1;
      if (ao.category === 'locking_pins') return (Number(ao.pinsLevels) || 0) * 4;
      return 0;
    }
    var ACCESSORY_NOUNS = { floor_bolts: 'bolt', leg_protectors: 'protector', barriers: 'barrier', locking_pins: 'pin' };
    function accessoryWorkingSentence(ao, qty) {
      if (ao.category === 'floor_bolts') return 'You need ' + qty + ' bolts — ' + ao.floorBoltFrames + ' frames × 4 per baseplate.';
      if (ao.category === 'leg_protectors') return 'You need ' + qty + ' protectors — ' + ao.protectorFrames + ' frames, ' + (ao.protectorCoverage === 'all' ? 'all uprights (2 per frame)' : 'front uprights only (1 per frame)') + '.';
      if (ao.category === 'barriers') return 'You need ' + qty + ' barrier' + (qty === 1 ? '' : 's') + ' — protecting ' + (ao.barrierRunEnds === 'both' ? 'both run ends' : 'one run end') + '.';
      if (ao.category === 'locking_pins') return 'You need ' + qty + ' pins — ' + ao.pinsLevels + ' levels × 4 per beam pair.';
      return '';
    }
    function accessoryBestMatchCardHTML(p, ao, qty) {
      if (ao.cartQty[p.variantId] === undefined) ao.cartQty[p.variantId] = qty;
      var cardQty = ao.cartQty[p.variantId];
      var confirmedQty = ao.confirmedAdds[p.variantId];
      var h = '<div class="rsi-uqb-v2__deck-card rsi-uqb-v2__deck-card--best">' +
        '<span class="rsi-uqb-v2__deck-card-badge">Best match</span>' +
        '<a class="rsi-uqb-v2__deck-card-link" href="/products/' + esc(p.handle) + '" title="' + esc(p.title) + '">' +
        '<img class="rsi-uqb-v2__deck-card-img" src="' + esc(p.image || '') + '" alt="' + esc(p.title) + '" loading="lazy">' +
        '<b class="rsi-uqb-v2__deck-card-name">' + esc(accessoryCardName(p)) + '</b>' +
        '</a>' +
        '<span class="rsi-uqb-v2__deck-card-dims">' + esc(accessoryMetaLine(p)) + '</span>';
      if (confirmedQty !== undefined) {
        h += basketConfirmBlockHTML(confirmedQty, p.priceGBP, ACCESSORY_NOUNS[ao.category] || 'item', 'data-rsi-uqb-v2-acc-add-else', 'Add something else');
      } else {
        h += '<div class="rsi-uqb-v2__deck-card-row">' +
          '<span class="rsi-uqb-v2__deck-card-price"><b>' + deckMoney(p.priceGBP) + '</b> each</span>' +
          '<div class="rsi-uqb-v2__stepper rsi-uqb-v2__stepper--sm" data-rsi-uqb-stepper>' +
          '<button type="button" data-rsi-uqb-step-down aria-label="Decrease">&minus;</button>' +
          '<input inputmode="numeric" value="' + esc(cardQty) + '" min="0" max="999" data-rsi-uqb-v2-deck-qty="' + p.variantId + '">' +
          '<button type="button" data-rsi-uqb-step-up aria-label="Increase">+</button></div></div>' +
          '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__deck-add rsi-uqb-v2__deck-add--best" data-rsi-uqb-v2-deck-add="' + p.variantId + '">Add all ' + cardQty + ' to basket — ' + deckMoney(cardQty * p.priceGBP) + '</button>';
      }
      h += '</div>';
      return h;
    }
    function accessoryResultsHTML(matches, ao) {
      var sorted = matches.slice().sort(function (a, b) { return a.priceGBP - b.priceGBP; });
      var best = sorted[0];
      var alts = sorted.slice(1);
      var qty = accessoryComputedQty(ao);
      var h = '<div class="rsi-uqb-v2__deck-results">';
      h += '<p class="rsi-uqb-v2__deck-working">' + esc(accessoryWorkingSentence(ao, qty)) + '</p>';
      h += accessoryBestMatchCardHTML(best, ao, qty);
      if (alts.length) {
        h += '<button type="button" class="rsi-uqb-v2__deck-alt-toggle" data-rsi-uqb-v2-acc-alt-toggle aria-expanded="' + (ao.altExpanded ? 'true' : 'false') + '">Other options — heavier duty &amp; finishes (' + alts.length + ')<span class="rsi-uqb-v2__deck-alt-chevron">' + (ao.altExpanded ? '&#9650;' : '&#9660;') + '</span></button>';
        h += '<div class="rsi-uqb-v2__deck-alts"' + (ao.altExpanded ? '' : ' hidden') + '>' + accessoryCardsHTML(alts, ao, qty) + '</div>';
      }
      h += '</div>';
      return h;
    }

    var STAGES = {
      need: function () {
        var opts = [
          { v: 'complete_run', icon: GLYPHS.run, b: 'Full racking bays', s: 'Complete, ready-to-load bays — frames, beams and fittings. From 1 bay to full warehouses.' },
          { v: 'frames_only', icon: GLYPHS.frame, b: 'Frames only', s: 'Just the uprights — to extend a run or replace damaged ones.' },
          { v: 'beams_only', icon: GLYPHS.beam, b: 'Beams only', s: 'Just the beams — add levels or replace on racking you already have.' },
          { v: 'decking_only', icon: GLYPHS.deck, b: 'Decking only', s: 'Timber, chipboard or mesh decks to sit on your beams.' },
          { v: 'accessories_only', icon: GLYPHS.acc, b: 'Accessories & safety', s: 'Floor bolts, leg protectors, barriers and load notices.' },
          { v: 'not_sure', icon: GLYPHS.help, b: 'Not sure — talk to us', s: 'Tell us your space and what you’re storing. We’ll spec it with you.' }
        ];
        return '<h2 class="rsi-uqb-v2__title">What do you need?</h2>' +
          '<p class="rsi-uqb-v2__sub">Pick one — the builder shapes itself around your answer.</p>' +
          cardsHTML('requestType', opts);
      },

      size: function () {
        if (state.requestType === 'frames_only') return STAGES._sizeFrames();
        if (state.requestType === 'beams_only') return STAGES._sizeBeams();
        if (state.requestType === 'decking_only') return STAGES._sizeDecking();
        if (state.requestType === 'accessories_only') return STAGES._sizeAcc();

        var h = '<div class="rsi-uqb-v2__title-row"><h2 class="rsi-uqb-v2__title">Size your run</h2>' + runBadgeHTML() + '</div>' +
          '<p class="rsi-uqb-v2__sub">Watch it take shape on the drawing as you go. Not sure of a size? Pick the closest — we true it up on the call.</p>';
        var rb = state.runAddedBanner;
        if (rb && rb.newRun === state.activeRunIndex + 1) {
          h += '<div class="rsi-uqb-v2__run-banner">' +
            '<span class="rsi-uqb-v2__run-banner-text">Run ' + rb.newRun + ' added — copied from Run ' + rb.fromRun + ', so just change what’s different. Your quote now combines ' + (rb.newRun === 2 ? 'both runs' : 'all ' + rb.newRun + ' runs') + '.</span>' +
            '<button type="button" class="rsi-uqb-v2__run-banner-dismiss" data-rsi-uqb-v2-run-banner-dismiss aria-label="Dismiss">&times;</button>' +
            '</div>';
        }
        h += stepperHTML('cr.bays', 1, 999, 'rsi-uqb-v2-cr-bays', 'Bays <small>— openings between uprights</small>');
        h += '<span class="rsi-uqb-v2__f-label">Frame height <small>— floor to top of upright, mm</small></span>' +
          customChipsHTML('cr.frameHeightMm', 'cr.frameHeightCustom', shortHeightOpts(), { id: 'rsi-uqb-v2-cr-height-custom', label: 'Height in mm', placeholder: 'e.g. 4200', hint: 'Up to 15,000mm — taller? Call us.' });
        var depthOpts = [{ v: 900, l: '900' }, { v: 1100, l: '1100' }];
        if (state.brand === 'pss') depthOpts.push({ v: 600, l: '600 (PSS only)' });
        h += '<span class="rsi-uqb-v2__f-label">Frame depth <small>— front to back; 900mm suits UK pallets</small></span>' + chipsHTML('cr.frameDepthMm', depthOpts);
        h += '<span class="rsi-uqb-v2__f-label">Beam length <small>— the width of each bay</small></span>' +
          customChipsHTML('cr.beamLengthKey', 'cr.beamLengthCustom', shortBeamOpts(), { id: 'rsi-uqb-v2-cr-beam-custom', label: 'Beam length in mm', placeholder: 'e.g. 2450', hint: 'Up to 4,000mm — longer? Call us.' });
        h += '<span class="rsi-uqb-v2__f-label">Beam levels <small>— floor storage doesn’t count</small></span>' +
          customChipsHTML('cr.beamLevels', 'cr.beamLevelsCustom', [1, 2, 3, 4, 5].map(function (n) { return { v: n, l: String(n) }; }), { id: 'rsi-uqb-v2-cr-levels-custom', label: 'Number of levels', placeholder: 'e.g. 8', hint: 'Up to 10 levels — more? Call us.' });
        h += '<span class="rsi-uqb-v2__f-label">Matching existing racking?</span>' + chipsHTML('matchingExisting', [{ v: 'no', l: 'No — new run' }, { v: 'yes', l: 'Yes — must match' }, { v: 'not_sure', l: 'Not sure' }], 'rsi-uqb-v2__chip--txt');
        if (state.matchingExisting !== 'no') {
          h += '<span class="rsi-uqb-v2__f-label">Brand, if you know it</span>' + chipsHTML('brand', BRAND_OPTS, 'rsi-uqb-v2__chip--txt');
          h += '<p class="rsi-uqb-v2__hint">We confirm compatibility before anything is quoted — a photo of a beam connector on the call is all it takes.</p>';
        }
        return h;
      },

      _sizeFrames: function () {
        var fo = state.framesOnly;
        var h = '<h2 class="rsi-uqb-v2__title">Frame details</h2>' +
          '<p class="rsi-uqb-v2__sub">Uprights to extend or replace what you have.</p>';
        h += stepperHTML('fo.quantity', 1, 999, 'rsi-uqb-v2-fo-qty', 'How many frames?');
        h += '<span class="rsi-uqb-v2__f-label">Frame height</span>' +
          customChipsHTML('fo.frameHeightMm', 'fo.frameHeightCustom', shortHeightOpts(), { id: 'rsi-uqb-v2-fo-height-custom', label: 'Height in mm', placeholder: 'e.g. 4200', hint: 'Up to 15,000mm — taller? Call us.' });
        h += '<span class="rsi-uqb-v2__f-label">Frame depth</span>' + chipsHTML('fo.frameDepthMm', [{ v: 900, l: '900' }, { v: 1100, l: '1100' }]);
        h += '<span class="rsi-uqb-v2__f-label">Matching existing racking?</span>' + chipsHTML('matchingExisting', [{ v: 'no', l: 'No' }, { v: 'yes', l: 'Yes — must match' }, { v: 'not_sure', l: 'Not sure' }], 'rsi-uqb-v2__chip--txt');
        if (state.matchingExisting !== 'no') h += '<span class="rsi-uqb-v2__f-label">Brand, if you know it</span>' + chipsHTML('brand', BRAND_OPTS, 'rsi-uqb-v2__chip--txt');
        return h;
      },

      _sizeBeams: function () {
        var h = '<h2 class="rsi-uqb-v2__title">Beam details</h2>' +
          '<p class="rsi-uqb-v2__sub">One level = one pair of beams.</p>';
        h += '<span class="rsi-uqb-v2__f-label">Beam length</span>' +
          customChipsHTML('bo.beamLengthKey', 'bo.beamLengthCustom', shortBeamOpts(), { id: 'rsi-uqb-v2-bo-beam-custom', label: 'Beam length in mm', placeholder: 'e.g. 2450', hint: 'Up to 4,000mm — longer? Call us.' });
        h += stepperHTML('bo.beamPairQuantity', 1, 999, 'rsi-uqb-v2-bo-pairs', 'Beam pairs');
        h += '<span class="rsi-uqb-v2__f-label">Include locking pins?</span>' + chipsHTML('bo.includeLockingPins', [{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">Which brand is your racking?</span>' + chipsHTML('brand', BRAND_OPTS, 'rsi-uqb-v2__chip--txt');
        return h;
      },

      _sizeDecking: function () {
        var d = state.deckingOnly;
        var h = '<h2 class="rsi-uqb-v2__title">Decking</h2>' +
          '<p class="rsi-uqb-v2__sub">Answer four quick questions — we’ll work out exactly what you need.</p>';
        h += '<span class="rsi-uqb-v2__f-label">Type</span>' + chipsHTML('do.type', [{ v: 'mesh', l: 'Mesh' }, { v: 'timber', l: 'Timber' }, { v: 'chipboard', l: 'Chipboard' }], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">Frame depth</span>' + chipsHTML('do.depthMm', [{ v: 900, l: '900' }, { v: 1100, l: '1100' }]);
        var widthOpts = shortBeamOpts().concat([{ v: 'not_sure', l: 'Not sure' }]);
        h += '<span class="rsi-uqb-v2__f-label">Bay width</span>' +
          customChipsHTML('do.beamLengthKey', 'do.beamLengthCustom', widthOpts, { id: 'rsi-uqb-v2-do-width-custom', label: 'Bay width in mm', placeholder: 'e.g. 2450', hint: 'Up to 4,000mm — longer? Call us.' });
        h += stepperHTML('do.bays', 1, 999, 'rsi-uqb-v2-do-bays', 'How many bays?');
        h += stepperHTML('do.levels', 1, 99, 'rsi-uqb-v2-do-levels', 'Levels to deck per bay?');
        return h;
      },

      _sizeAcc: function () {
        var ao = state.accessoriesOnly;
        var h = '<h2 class="rsi-uqb-v2__title">Accessories &amp; safety</h2>' +
          '<p class="rsi-uqb-v2__sub">Answer a couple of quick questions — we’ll work out exactly what you need.</p>';
        h += '<span class="rsi-uqb-v2__f-label">What do you need?</span>' + chipsHTML('ao.category', [
          { v: 'floor_bolts', l: 'Floor bolts' },
          { v: 'leg_protectors', l: 'Leg protectors' },
          { v: 'barriers', l: 'Barriers' },
          { v: 'load_notices', l: 'Load notices' },
          { v: 'locking_pins', l: 'Locking pins' }
        ], 'rsi-uqb-v2__chip--txt');
        if (ao.category === 'floor_bolts') {
          h += stepperHTML('ao.floorBoltFrames', 1, 999, 'rsi-uqb-v2-ao-frames', 'How many frames?');
        } else if (ao.category === 'leg_protectors') {
          h += stepperHTML('ao.protectorFrames', 1, 999, 'rsi-uqb-v2-ao-pframes', 'How many frames?');
          h += '<span class="rsi-uqb-v2__f-label">Coverage</span>' + chipsHTML('ao.protectorCoverage', [{ v: 'front', l: 'Front uprights only' }, { v: 'all', l: 'All uprights' }], 'rsi-uqb-v2__chip--txt');
        } else if (ao.category === 'barriers') {
          h += '<span class="rsi-uqb-v2__f-label">Run ends to protect?</span>' + chipsHTML('ao.barrierRunEnds', [{ v: 'one', l: 'One' }, { v: 'both', l: 'Both' }], 'rsi-uqb-v2__chip--txt');
        } else if (ao.category === 'locking_pins') {
          h += '<span class="rsi-uqb-v2__f-label">Which brand?</span>' + chipsHTML('ao.lockingPinsBrand', [
            { v: 'Link 51', l: 'Link 51' },
            { v: 'Redirack', l: 'Redirack' },
            { v: 'Dexion', l: 'Dexion' },
            { v: 'Mecalux', l: 'Mecalux' },
            { v: 'Schaefer', l: 'Schaefer' },
            { v: 'not_sure', l: 'Not sure' }
          ], 'rsi-uqb-v2__chip--txt');
          h += stepperHTML('ao.pinsLevels', 1, 99, 'rsi-uqb-v2-ao-pinlevels', 'How many beam levels?');
        }
        return h;
      },

      onracking: function () {
        var result = calculateGuideEstimate(state);
        var cr = state.completeRun;
        var frames = cr.bays > 0 ? Number(cr.bays) + 1 : 0;
        var h = '<div class="rsi-uqb-v2__title-row"><h2 class="rsi-uqb-v2__title">On the racking</h2>' + runBadgeHTML() + '</div>' +
          '<p class="rsi-uqb-v2__sub">What sits on it, how heavy, and what protection you need — the drawing and guide price update as you go.</p>';
        h += '<span class="rsi-uqb-v2__f-label">What sits on the beams?</span>' + chipsHTML('cr.onRacking', [
          { v: 'uk_standard', l: 'UK pallets · 1200×1000' },
          { v: 'euro', l: 'Euro pallets · 1200×800' },
          { v: 'timber', l: 'Timber decking' },
          { v: 'chipboard', l: 'Chipboard decking' },
          { v: 'mesh', l: 'Mesh decking' },
          { v: 'none', l: 'Not needed' }
        ], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">Max load per beam level</span>' + chipsHTML('cr.maxLoadPerLevelKg', LOAD_PER_BEAM_OPTS, 'rsi-uqb-v2__chip--txt');
        if (result.warnings.some(function (w) { return w.key === 'beam_capacity' && (state.runs.length < 2 || w.runIndex === state.activeRunIndex); })) {
          h += '<div class="rsi-uqb-v2__anno"><b>NOTE</b><span>That load may exceed this beam duty. Nothing to fix now — heavier-duty beams exist in most sizes, and we’ll confirm the right one on the call.</span></div>';
        }
        h += '<span class="rsi-uqb-v2__f-label">Floor fixings <small>— ' + (frames * 4) + ' bolts for your ' + frames + ' frames</small></span>' + chipsHTML('cr.floorBolts', [{ v: 'no', l: 'No' }, { v: 'yes', l: 'Yes' }], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">Frame leg protectors</span>' + chipsHTML('cr.frameProtectors', [{ v: 'none', l: 'None' }, { v: 'front', l: 'Front uprights · ' + frames }, { v: 'all', l: 'All uprights · ' + (frames * 2) }], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">Load notice board <small>— SEMA advises one per run</small></span>' + chipsHTML('cr.loadNotice', [{ v: 'no', l: 'No' }, { v: 'yes', l: 'Yes' }], 'rsi-uqb-v2__chip--txt');
        h += '<span class="rsi-uqb-v2__f-label">End barriers</span>' + chipsHTML('cr.endBarriers', [{ v: 'no', l: 'No' }, { v: 'single', l: 'One end' }, { v: 'double', l: 'Both ends' }], 'rsi-uqb-v2__chip--txt');
        h += '<div class="rsi-uqb-v2__anno rsi-uqb-v2__anno--info"><b>INFO</b><span>Locking pins included and priced below — stock confirmed on the call.</span></div>';
        h += '<div class="rsi-uqb-v2__multirun-explainer">' +
          '<h3 class="rsi-uqb-v2__multirun-explainer-title">Need more than one run of racking?</h3>' +
          '<p class="rsi-uqb-v2__multirun-explainer-text">A run is a row of connected bays. Add as many runs as you need, each with its own sizes — everything combines into one quote.</p>' +
          (state.runs.length >= 6
            ? '<a href="#" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--secondary" data-rsi-uqb-v2-trade-link>Larger project? Get trade pricing</a>'
            : '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--secondary" data-rsi-uqb-v2-run-add>' + (state.runs.length < 2 ? '+ Add a second run' : '+ Add another run') + '</button>') +
          '</div>';
        return h;
      },

      delivery: function () {
        var h = '<h2 class="rsi-uqb-v2__title">Getting it to you</h2>' +
          '<p class="rsi-uqb-v2__sub">Guide price is for the racking — delivery is confirmed on the call, no surprises.</p>';
        h += '<span class="rsi-uqb-v2__f-label">What do you need?</span>' + chipsHTML('delivery.requirement', [{ v: 'supply_only', l: 'Supply only' }, { v: 'supply_install', l: 'Supply + installation' }, { v: 'survey_not_sure', l: 'Site survey / not sure' }], 'rsi-uqb-v2__chip--txt');
        h += '<div class="rsi-uqb-v2__two"><div><span class="rsi-uqb-v2__f-label">Delivery postcode</span>' + textHTML('delivery.postcode', { autocomplete: 'postal-code', placeholder: 'e.g. CV13 6PA' }) + '</div>' +
          '<div><span class="rsi-uqb-v2__f-label">Unloading on site</span>' + chipsHTML('delivery.unloading', [{ v: 'forklift', l: 'Forklift' }, { v: 'manual', l: 'By hand' }, { v: 'not_sure', l: 'Not sure' }], 'rsi-uqb-v2__chip--txt') + '</div></div>';
        h += '<p class="rsi-uqb-v2__hint">Delivery typically £85–£150 under 50 miles · £150–£250 to 100 · £250–£400 to 200.</p>';
        h += '<span class="rsi-uqb-v2__f-label">When do you need it?</span>' + chipsHTML('delivery.timeframe', [{ v: 'asap', l: 'ASAP' }, { v: 'one_week', l: 'Within a week' }, { v: 'two_to_four_weeks', l: '2–4 weeks' }, { v: 'flexible', l: 'Flexible' }], 'rsi-uqb-v2__chip--txt');
        return h;
      },

      space: function () {
        return '<h2 class="rsi-uqb-v2__title">Tell us your space</h2>' +
          '<p class="rsi-uqb-v2__sub">No measurements needed — a rough idea is plenty. We’ll spec and draw it with you on the call.</p>' +
          '<label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-space">Your space, in your words</label>' +
          '<input class="rsi-uqb-v2__tin" type="text" id="rsi-uqb-v2-space" name="contact[approx_space]" placeholder="e.g. 15m along one wall, about 6m high, storing mixed pallets">';
      },

      send: function () {
        var h = '<h2 class="rsi-uqb-v2__title">Send it to the team</h2>' +
          '<p class="rsi-uqb-v2__sub">Last step — your drawing and guide price go straight to ' + esc(root.getAttribute('data-rsi-uqb-v2-contact-name') || 'the team') + '. One call confirms everything.</p>';
        h += '<div class="rsi-uqb-v2__two"><div><label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-name">Name</label>' +
          textHTML('customer.name', { id: 'rsi-uqb-v2-name', name: 'contact[name]', required: true, autocomplete: 'name' }) +
          '<p class="rsi-uqb-v2__field-error" id="rsi-uqb-v2-name-error" role="alert" hidden></p></div>' +
          '<div><label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-company">Company <small>— optional</small></label>' +
          textHTML('customer.company', { id: 'rsi-uqb-v2-company', name: 'contact[company]', autocomplete: 'organization' }) + '</div></div>';
        h += '<div class="rsi-uqb-v2__two"><div><label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-email">Email</label>' +
          textHTML('customer.email', { id: 'rsi-uqb-v2-email', name: 'contact[email]', type: 'email', required: true, autocomplete: 'email' }) +
          '<p class="rsi-uqb-v2__field-error" id="rsi-uqb-v2-email-error" role="alert" hidden></p></div>' +
          '<div><label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-phone">Mobile <small>— for your quote by text</small></label>' +
          textHTML('customer.phone', { id: 'rsi-uqb-v2-phone', name: 'contact[phone]', type: 'tel', autocomplete: 'tel' }) + '</div></div>';
        h += '<label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-postcode" style="max-width:220px">Your postcode</label>' +
          textHTML('customer.postcode', { id: 'rsi-uqb-v2-postcode', name: 'contact[postcode]', required: true, autocomplete: 'postal-code', style: 'max-width:220px' }) +
          '<p class="rsi-uqb-v2__field-error" id="rsi-uqb-v2-postcode-error" role="alert" hidden></p>';
        h += '<span class="rsi-uqb-v2__f-label">How would you like to proceed?</span><div class="rsi-uqb-v2__chips" role="radiogroup">' +
          CALLBACK_OPTS.map(function (o, i) {
            return '<label class="rsi-uqb-v2__chip rsi-uqb-v2__chip--txt"><input class="rsi-uqb-v2__native" type="radio" name="contact[callback_preference]" value="' + o.v + '" data-rsi-p1-callback' + (i === 0 ? ' checked' : '') + '>' + o.l + '</label>';
          }).join('') + '</div>';
        h += '<label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-notes">Anything else? <small>— optional</small></label>' +
          textHTML('customer.notes', { id: 'rsi-uqb-v2-notes', name: 'contact[notes]', placeholder: 'Access restrictions, existing brand, photos to send…' });
        // Same field name/option list as snippets/rsi-referral-source-field.liquid
        // (kept in sync by hand — this wizard's whole UI is client-side
        // string-templated, so it can't {% render %} that snippet directly).
        h += '<label class="rsi-uqb-v2__f-label" for="rsi-uqb-v2-referral">How did you hear about us? <small>— optional</small></label>' +
          '<select class="rsi-uqb-v2__tin" id="rsi-uqb-v2-referral" name="contact[How did you hear about us?]">' +
          ['Select an option', 'Google Search', 'Google / Social Media Ads', 'Facebook / Instagram', 'LinkedIn', 'Amazon', 'YouTube', 'A friend or colleague', 'Existing customer', 'Trade show or exhibition', 'Other']
            .map(function (label, i) { return '<option' + (i === 0 ? ' value=""' : '') + '>' + label + '</option>'; }).join('') +
          '</select>';
        h += '<label class="rsi-uqb-v2__f-label" style="display:flex;gap:10px;align-items:flex-start;font-weight:500;cursor:pointer;margin-top:18px">' +
          '<input type="checkbox" id="rsi-uqb-v2-acknowledged" name="contact[acknowledged_guide_estimate]" value="yes" data-rsi-uqb-field="acknowledged"' + (state.acknowledgedGuideEstimate ? ' checked' : '') + ' style="margin-top:3px;width:17px;height:17px;flex:0 0 auto;accent-color:var(--rsi-uqb-orange, #cd3800)">' +
          'I understand this is a guide price — confirmed before anything is sent.</label>' +
          '<p class="rsi-uqb-v2__field-error" id="rsi-uqb-v2-acknowledged-error" role="alert" hidden></p>';
        h += '<p class="rsi-uqb-v2__promise"><strong>Our promise:</strong> if anything changes when we’re in touch, we tell you exactly why — and we work to beat the guide on quantity, never quietly add to it.</p>';
        return h;
      }
    };

    // "No preference" leads (not trailing) and is the state default (see
    // defaultState().brand below) — most customers have no idea what
    // brand their racking is, and having to actively hunt for "Not sure"
    // at the end of a list of unfamiliar brand names read as if a brand
    // pick was expected. Value stays 'not_sure' (unchanged) so the
    // existing pricing fallback (resolveBrand(), which already treats
    // 'not_sure' same as 'other') keeps working without any other edit.
    var BRAND_OPTS = [
      { v: 'not_sure', l: 'No preference' }, { v: 'link51', l: 'Link 51' }, { v: 'redirack', l: 'Redirack' }, { v: 'dexion_p90', l: 'Dexion P90' },
      { v: 'mecalux', l: 'Mecalux' }, { v: 'hilo', l: 'Hilo' }, { v: 'schaefer', l: 'Schaefer' }, { v: 'pss', l: 'PSS' }, { v: 'other', l: 'Other' }
    ];

    // "How would you like to proceed?" on the final Send step. The first
    // three keep their original values/order (call-time preference,
    // unchanged behaviour); the last two are new fast-track outcomes so a
    // customer who already knows what they want isn't forced through a
    // phone call to get a firm figure. assets/rsi-uqb-phase1.js's
    // populateReceipt() branches on these same values to show the right
    // "what happens next" copy on the receipt screen.
    var CALLBACK_OPTS = [
      { v: 'asap', l: 'Call me ASAP' }, { v: 'this_afternoon', l: 'Call this afternoon' }, { v: 'tomorrow_morning', l: 'Call tomorrow morning' },
      { v: 'email_quote', l: 'Email me a firm quote' }, { v: 'ready_to_purchase', l: 'I’m ready to buy — send an invoice' }
    ];

    // Every value is the bucket's own upper bound (matches how
    // checkBeamCapacity() already consumes this field — see below), so no
    // choice here can ever resolve to a real load of 0kg. 0 stays reserved
    // purely as the "nothing picked yet" state default (defaultState().
    // completeRun.maxLoadPerLevelKg) and is never a selectable option, so
    // checkBeamCapacity()'s `if (!maxLoad) return null` correctly means
    // "no answer yet" and nothing else. The top bucket is open-ended
    // ("2000kg+"), so it uses 3000 — the heaviest duty rating this
    // catalogue actually stocks (see BEAM_PRICES above) — which makes the
    // "may exceed this beam's rated duty" check fire for every beam
    // except the very few already rated to that ceiling, rather than
    // silently passing an unknown-but-possibly-heavier load.
    var LOAD_PER_BEAM_OPTS = [
      { v: 500, l: 'Under 500kg' }, { v: 1000, l: '500 - 1000kg' }, { v: 1500, l: '1000 - 1500kg' },
      { v: 2000, l: '1500 - 2000kg' }, { v: 3000, l: '2000kg+' }
    ];

    /* ---- In-card progress stepper: done (clickable) / now (highlighted,
       gets the "N of TOTAL · Label" mobile-only text) / locked (muted,
       not clickable) — nothing ahead of the current stage can ever be
       reached except by advancing through Continue. Dots joined by a
       connecting line (CSS ::before/::after on .rsi-uqb-v2__sstep,
       filled up to the current step) — see rsi-uqb-v2.css. ---- */
    function renderRail() {
      if (!railHost) return;
      var f = flow();
      railHost.innerHTML = f.map(function (id, i) {
        var cls = i < stageIx ? 'is-done' : i === stageIx ? 'is-now' : 'is-locked';
        var go = i < stageIx ? ' data-rsi-uqb-v2-go="' + i + '"' : '';
        var mobileLabel = i === stageIx ? '<span class="rsi-uqb-v2__sstep-mobile">' + (i + 1) + ' of ' + f.length + ' &middot; ' + STAGE_NAMES[id] + '</span>' : '';
        return '<span class="rsi-uqb-v2__sstep ' + cls + '"' + go + '>' +
          '<span class="rsi-uqb-v2__sstep-dot">' + (i + 1) + '</span>' +
          '<span class="rsi-uqb-v2__sstep-label">' + STAGE_NAMES[id] + '</span>' +
          mobileLabel +
          '</span>';
      }).join('');
    }

    function navHTML() {
      var f = flow();
      var last = stageIx === f.length - 1;
      var first = stageIx === 0;
      /* Step 1, "Not sure" selected: the contact card (Call/Email/
         WhatsApp) is the only path forward, so there's no Continue
         to press and nothing being priced yet to update live. */
      var deckPhase1 = f[stageIx] === 'size' && state.requestType === 'decking_only' && !state.deckingOnly.resultsShown;
      var deckCardsShowing = f[stageIx] === 'size' && state.requestType === 'decking_only' && state.deckingOnly.resultsShown && !isDeckingFallback(state.deckingOnly) && !deckCatalogueLoading && matchDeckProducts(state.deckingOnly).length > 0;
      var accPhase1 = f[stageIx] === 'size' && state.requestType === 'accessories_only' && !state.accessoriesOnly.resultsShown;
      var accCardsShowing = f[stageIx] === 'size' && state.requestType === 'accessories_only' && state.accessoriesOnly.resultsShown && !accessoriesCatalogueLoading && matchAccessoryProducts(state.accessoriesOnly).length > 0;
      var hideForward = (stageIx === 0 && state.requestType === 'not_sure') || deckCardsShowing || accCardsShowing;
      var h = '<div class="rsi-uqb-v2__nav">';
      if (!first) h += '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--back" data-rsi-uqb-v2-nav-back>&larr; Back</button>';
      if (deckPhase1) {
        h += '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go" data-rsi-uqb-v2-deck-show-results>Show me what I need &rarr;</button>';
        h += '<span class="rsi-uqb-v2__nav-sla">We’ll work out exactly what you need &rarr;</span>';
      } else if (accPhase1) {
        h += '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go" data-rsi-uqb-v2-acc-show-results>Show me what I need &rarr;</button>';
        h += '<span class="rsi-uqb-v2__nav-sla">We’ll work out exactly what you need &rarr;</span>';
      } else if (!hideForward) {
        h += '<button type="' + (last ? 'submit' : 'button') + '" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go"' + (last ? ' data-rsi-uqb-submit' : ' data-rsi-uqb-v2-nav-next') + '>' +
          (last ? esc(root.getAttribute('data-rsi-uqb-v2-primary-cta') || 'Send my quote request') : 'Continue &rarr;') + '</button>';
        h += '<span class="rsi-uqb-v2__nav-sla">' + (last
          ? 'Send now and we’ll call you <b data-rsi-p1-sla-inline>within 1 working hour</b>. Open 7 days, 8am–5pm.'
          : 'Guide price updates live &rarr;') + '</span>';
      }
      h += '</div>';
      return h;
    }

    /* ---- Mobile bottom-bar CTA — mirrors navHTML()'s forward action
       (Continue / "Show me what I need" / Send) into the fixed bar so
       it's reachable at thumb height, without duplicating navHTML's
       Back button or SLA copy. In decking/accessories' matched
       (Phase 2) view the in-card nav hides its forward button entirely
       — the real actions are the per-product Add buttons inside the
       mobile sheet — so this renders a basket CTA that opens that
       sheet (or "View basket" once something's in it) instead. Reads
       productMode from sheetPanel()'s return (computed once inside
       refreshSheet()) rather than re-deriving deck/acc phase state
       itself — one dispatch, read everywhere. ---- */
    function mbarCtaHTML(productMode) {
      if (productMode === 'deck-matched' || productMode === 'acc-matched') {
        var summary = productMode === 'deck-matched' ? deckCartSummary() : accessoryCartSummary();
        if (summary.count) {
          return '<a class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn" href="/cart">View basket &rarr;</a>';
        }
        return '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn" data-rsi-uqb-v2-mbar-open-sheet>Add to basket &rarr;</button>';
      }
      /* Step 1 "Not sure": no single forward action exists in-card
         either (Call/Email/WhatsApp are the only paths) — mirror
         that by rendering nothing. */
      if (stageIx === 0 && state.requestType === 'not_sure') return '';
      if (productMode === 'deck-placeholder') return '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn" data-rsi-uqb-v2-deck-show-results>Continue &rarr;</button>';
      if (productMode === 'acc-placeholder') return '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn" data-rsi-uqb-v2-acc-show-results>Continue &rarr;</button>';
      var f = flow();
      var last = stageIx === f.length - 1;
      if (last) return '<button type="submit" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn"' + (formEl && formEl.id ? ' form="' + esc(formEl.id) + '"' : '') + ' data-rsi-uqb-submit>Send</button>';
      return '<button type="button" class="rsi-uqb-v2__btn rsi-uqb-v2__btn--go rsi-uqb-v2__mbar-cta-btn" data-rsi-uqb-v2-nav-next>Continue &rarr;</button>';
    }

    /* ============================================================
       DRAWING — photoreal elevation, reading `state` directly.
       ============================================================ */
    function svgDim(x1, y1, x2, y2, label, vert, stl) {
      var t = '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="' + stl + '" stroke-width="1"/>';
      var tick = 4;
      if (vert) {
        t += '<line x1="' + (x1 - tick) + '" y1="' + y1 + '" x2="' + (x1 + tick) + '" y2="' + y1 + '" stroke="' + stl + '"/><line x1="' + (x1 - tick) + '" y1="' + y2 + '" x2="' + (x1 + tick) + '" y2="' + y2 + '" stroke="' + stl + '"/>';
        t += '<text x="' + (x1 - 8) + '" y="' + ((y1 + y2) / 2) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="11" text-anchor="middle" transform="rotate(-90 ' + (x1 - 8) + ' ' + ((y1 + y2) / 2) + ')">' + label + '</text>';
      } else {
        t += '<line x1="' + x1 + '" y1="' + (y1 - tick) + '" x2="' + x1 + '" y2="' + (y1 + tick) + '" stroke="' + stl + '"/><line x1="' + x2 + '" y1="' + (y1 - tick) + '" x2="' + x2 + '" y2="' + (y1 + tick) + '" stroke="' + stl + '"/>';
        t += '<text x="' + ((x1 + x2) / 2) + '" y="' + (y1 + 15) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="11" text-anchor="middle">' + label + '</text>';
      }
      return t;
    }

    function drawSvg(result) {
      var W = 560, H = 380, ml = 52, mr = 18, mt = 34, mb = 46;
      var g = '<svg viewBox="0 0 ' + W + ' ' + H + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Elevation drawing of your racking">';
      var ink = '#16224A', org = 'var(--rsi-uqb-orange, #cd3800)', stl = '#67718C', ln = '#B9C1D6';
      var innerW = W - ml - mr, innerH = H - mt - mb;
      var yG = H - mb;

      if (state.requestType === 'not_sure') {
        g += '<text x="' + (W / 2) + '" y="' + (H / 2 - 8) + '" text-anchor="middle" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="13">[ drawing pending ]</text>';
        g += '<text x="' + (W / 2) + '" y="' + (H / 2 + 14) + '" text-anchor="middle" fill="' + stl + '" font-size="12">We’ll draw this with you on the call.</text>';
        return g + '</svg>';
      }

      if (state.requestType === 'accessories_only') {
        var ao = state.accessoriesOnly;
        var cx = W / 2 - 60;
        g += '<line x1="' + ml + '" y1="' + yG + '" x2="' + (W - mr) + '" y2="' + yG + '" stroke="' + ink + '" stroke-width="2"/>';
        g += IM('upright', cx, yG - 200, 16, 200);
        g += IM('basePlate', cx - 10, yG - 16, 36, 16);
        if (ao.protectorQuantity > 0) g += IM('guard', cx - 9, yG - 42, 34, 42);
        if (ao.floorBoltQuantity > 0) { g += '<rect x="' + (cx - 14) + '" y="' + (yG - 13) + '" width="4" height="13" fill="' + org + '"/><rect x="' + (cx + 26) + '" y="' + (yG - 13) + '" width="4" height="13" fill="' + org + '"/>'; }
        if (ao.loadNoticeQuantity > 0) {
          g += '<rect x="' + (cx + 78) + '" y="' + (yG - 160) + '" width="72" height="54" rx="4" fill="#fff" stroke="' + ink + '" stroke-width="2"/>';
          g += '<text x="' + (cx + 114) + '" y="' + (yG - 136) + '" text-anchor="middle" fill="' + ink + '" font-family="IBM Plex Mono, monospace" font-size="9">LOAD</text>';
          g += '<text x="' + (cx + 114) + '" y="' + (yG - 124) + '" text-anchor="middle" fill="' + org + '" font-family="IBM Plex Mono, monospace" font-size="9">NOTICE</text>';
        }
        if (ao.endBarriers !== 'no') g += IM('barrier', cx + 170, yG - 56, 28, 56);
        g += '<text x="' + ml + '" y="' + (mt - 8) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="10" letter-spacing="2">DETAIL · UPRIGHT BASE</text>';
        return g + '</svg>';
      }

      if (state.requestType === 'frames_only') {
        var fo = state.framesOnly;
        var foHeight = Number(fo.frameHeightMm) || 5000;
        var foDepth = Number(fo.frameDepthMm) || 900;
        var sc2 = innerH / Math.max(foHeight, 2600) * 0.98;
        var h2 = foHeight * sc2;
        var upW2 = Math.max(90 * sc2, 8);
        var gapPx = Math.max(foDepth * sc2, 30);
        var footH2 = Math.max(70 * sc2, 8), footW2 = footH2 * 1.45;
        var fx1 = ml + 24, fx2 = fx1 + upW2 + gapPx;
        var frameRight = fx2 + upW2;
        var topY = yG - h2;

        g += '<line x1="' + ml + '" y1="' + yG + '" x2="' + (W - mr) + '" y2="' + yG + '" stroke="' + ink + '" stroke-width="2"/>';

        /* Bracing drawn behind the uprights, connecting at their inner
           faces — one continuous zig-zag, roughly one direction-change
           per metre of height, plus a horizontal brace top and bottom. */
        var braceColor = '#9AA3B5';
        var braceStroke = Math.max(upW2 * 0.6, 2);
        var leftFace = fx1 + upW2, rightFace = fx2;
        var inset = Math.max(h2 * 0.03, 4);
        var braceTopY = topY + inset, braceBotY = yG - inset;
        var segments = Math.max(2, Math.min(20, Math.round(foHeight / 1000)));
        var segH = Math.max(braceBotY - braceTopY, 1) / segments;
        g += '<line x1="' + leftFace + '" y1="' + braceTopY + '" x2="' + rightFace + '" y2="' + braceTopY + '" stroke="' + braceColor + '" stroke-width="' + braceStroke + '" stroke-linecap="round"/>';
        g += '<line x1="' + leftFace + '" y1="' + braceBotY + '" x2="' + rightFace + '" y2="' + braceBotY + '" stroke="' + braceColor + '" stroke-width="' + braceStroke + '" stroke-linecap="round"/>';
        var zpath = 'M ' + leftFace + ' ' + braceTopY;
        for (var zi = 0; zi < segments; zi++) {
          var zy = braceTopY + segH * (zi + 1);
          zpath += ' L ' + ((zi % 2 === 0) ? rightFace : leftFace) + ' ' + zy;
        }
        g += '<path d="' + zpath + '" fill="none" stroke="' + braceColor + '" stroke-width="' + braceStroke + '" stroke-linecap="round" stroke-linejoin="round"/>';

        g += IM('upright', fx1, topY, upW2, h2);
        g += IM('upright', fx2, topY, upW2, h2);
        g += IM('basePlate', fx1 + upW2 / 2 - footW2 / 2, yG - footH2, footW2, footH2);
        g += IM('basePlate', fx2 + upW2 / 2 - footW2 / 2, yG - footH2, footW2, footH2);

        g += svgDim(ml - 16, topY, ml - 16, yG, foHeight.toLocaleString(), true, stl);
        g += svgDim(fx1, yG + 14, frameRight, yG + 14, foDepth.toLocaleString(), false, stl);

        var labelX = frameRight + Math.max(24, (W - mr - frameRight) / 2);
        g += '<text x="' + labelX + '" y="' + (topY + h2 / 2 + 7) + '" text-anchor="middle" fill="' + ink + '" font-family="IBM Plex Mono, monospace" font-size="20" font-weight="600">&#215;&#160;' + fo.quantity + '</text>';

        g += '<text x="' + ml + '" y="' + (mt - 8) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="10" letter-spacing="2">FRAME · SIDE ELEVATION</text>';
        return g + '</svg>';
      }

      if (state.requestType === 'beams_only') {
        var bo = state.beamsOnly;
        var beamMmBO = BEAM_MM[bo.beamLengthKey];
        if (beamMmBO === undefined) beamMmBO = Math.max(900, Math.min(4500, Number(bo.beamLengthKey) || 2700));
        var scBeam = (innerW * 0.55) / 4500;
        var bwBO = beamMmBO * scBeam, bhBO = 30;
        var bxBO = ml + 24, byBO = H / 2 - bhBO / 2;
        var eWBO = Math.min(bhBO * 1.15, bwBO * 0.16);
        g += IM('beamM', bxBO, byBO, bwBO, bhBO);
        g += IM('beamL', bxBO, byBO, eWBO, bhBO);
        g += IM('beamR', bxBO + bwBO - eWBO, byBO, eWBO, bhBO);
        var beamRightBO = bxBO + bwBO;
        g += svgDim(bxBO, byBO + bhBO + 14, beamRightBO, byBO + bhBO + 14, beamMmBO.toLocaleString(), false, stl);
        var labelXBO = beamRightBO + Math.max(24, (W - mr - beamRightBO) / 2);
        g += '<text x="' + labelXBO + '" y="' + (byBO + bhBO / 2 + 7) + '" text-anchor="middle" fill="' + ink + '" font-family="IBM Plex Mono, monospace" font-size="20" font-weight="600">&#215;&#160;' + bo.beamPairQuantity + ' pairs</text>';
        g += '<text x="' + ml + '" y="' + (mt - 8) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="10" letter-spacing="2">BEAM PAIR · FRONT</text>';
        return g + '</svg>';
      }

      if (state.requestType === 'decking_only') {
        var doState = state.deckingOnly;
        var bw2 = innerW * 0.84, bx2 = ml + (innerW - bw2) / 2, bh2 = 30, mid = H / 2;
        [mid - 34, mid + 22].forEach(function (by) {
          g += IM('beamM', bx2, by, bw2, bh2);
          var eW = Math.min(bh2 * 1.15, bw2 * 0.16);
          g += IM('beamL', bx2, by, eW, bh2);
          g += IM('beamR', bx2 + bw2 - eW, by, eW, bh2);
          g += IM('beamTop', bx2, by - 6, bw2, 6);
          for (var dx = bx2 + 8; dx < bx2 + bw2 - 4; dx += 16) g += '<line x1="' + dx + '" y1="' + (by - 6) + '" x2="' + dx + '" y2="' + by + '" stroke="#fff" stroke-width="1" opacity=".5"/>';
        });
        var mmD = BEAM_MM[doState.beamLengthKey];
        if (mmD === undefined) mmD = Math.max(900, Math.min(4500, Number(doState.beamLengthKey) || 2700));
        g += svgDim(bx2, mid + 66, bx2 + bw2, mid + 66, mmD.toLocaleString(), false, stl);
        g += '<text x="' + ml + '" y="' + (mt - 8) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="10" letter-spacing="2">DECKED BEAM PAIR</text>';
        return g + '</svg>';
      }

      /* complete_run */
      var cr = state.completeRun;
      var bays = Number(cr.bays) || 0;
      var levels = Number(cr.beamLevels) || 0;
      var drawLevels = Math.min(levels, 5);
      var beamMm = BEAM_MM[cr.beamLengthKey];
      if (beamMm === undefined) beamMm = Math.max(900, Math.min(4500, Number(cr.beamLengthKey) || 2700));
      var upMm = 90;
      var drawBays = Math.max(1, Math.min(bays, 8)), extra = bays - drawBays;
      var runMm = drawBays * beamMm + (drawBays + 1) * upMm;
      var hMm = Number(cr.frameHeightMm) || 5000;
      /* End barriers stand outside the run, to the left and/or right of
         the end uprights. Reserve real pixel width for them BEFORE
         computing the scale — otherwise the run fills the full canvas,
         the barrier gets drawn past the edge, and the safety clamp
         below pulls it back in on top of the last bay's contents. */
      var barrierResPx = cr.endBarriers === 'double' ? 34 : (cr.endBarriers === 'single' ? 34 : 0);
      var barrierResL = cr.endBarriers === 'double' ? barrierResPx : 0;
      var barrierResR = cr.endBarriers !== 'no' ? barrierResPx : 0;
      var sc = Math.min((innerW - barrierResL - barrierResR) / runMm, innerH / hMm);
      var x0 = ml + barrierResL + (innerW - barrierResL - barrierResR - runMm * sc) / 2;
      var upW = Math.max(upMm * sc, 6), bmH = Math.max(130 * sc, 7);
      var footH = Math.max(70 * sc, 8), footW = footH * 1.45;
      var palKey = cr.palletType === 'euro' ? 'palletEuro' : 'palletUK';
      var fit = PALLETS_PER_LEVEL[cr.beamLengthKey], per = fit ? (fit[cr.palletType] || fit.mixed) : 2;
      var pitch = (hMm * 0.92) * sc / Math.max(drawLevels, 1);
      var showPallet = (cr.onRacking === 'uk_standard' || cr.onRacking === 'euro');
      var showDeck = (cr.onRacking === 'chipboard' || cr.onRacking === 'mesh' || cr.onRacking === 'timber');

      g += '<line x1="' + ml + '" y1="' + yG + '" x2="' + (W - mr) + '" y2="' + yG + '" stroke="' + ink + '" stroke-width="2"/>';
      for (var u = 0; u <= drawBays; u++) {
        var ux = x0 + u * (beamMm + upMm) * sc;
        g += IM('upright', ux, yG - hMm * sc, upW, hMm * sc);
      }
      for (var b = 0; b < drawBays; b++) {
        var bx0 = x0 + upW + b * (beamMm + upMm) * sc, bw = beamMm * sc, slotW = bw / per;
        for (var l = 0; l <= drawLevels; l++) {
          var baseY = (l === 0) ? yG : yG - pitch * l;
          var gapH = (l === drawLevels) ? pitch * 0.9 : pitch;
          var deckH = showDeck ? Math.max(3, 50 * sc) : 0;
          if (l > 0) {
            g += IM('beamM', bx0, baseY, bw, bmH);
            var eW2 = Math.min(bmH * 1.15, bw * 0.16);
            g += IM('beamL', bx0, baseY, eW2, bmH);
            g += IM('beamR', bx0 + bw - eW2, baseY, eW2, bmH);
            if (showDeck) g += IM('beamTop', bx0, baseY - deckH, bw, deckH);
          }
          var surfaceY = baseY - deckH;
          var palH = Math.max(150 * sc, 5);
          var stackH = showPallet ? palH : 0;
          var avail = gapH - bmH - deckH - stackH - 6;
          if (slotW > 7 && (!showPallet || palH > 4)) {
            for (var p = 0; p < per; p++) {
              var px = bx0 + p * slotW + 2, pw = slotW - 4;
              var boxBaseY = surfaceY;
              if (showPallet) { g += IM(palKey, px, surfaceY - palH, pw, palH); boxBaseY = surfaceY - palH; }
              var fbox = [0.62, 0.5, 0.4][(b * 13 + l * 7 + p * 5) % 3], bh = avail * fbox;
              bh = Math.min(bh, boxBaseY - (mt + 16));
              if (bh > 6) g += IM('box', px + 1, boxBaseY - bh, pw - 2, bh);
            }
          }
        }
      }
      for (var u2 = 0; u2 <= drawBays; u2++) {
        var ux2 = x0 + u2 * (beamMm + upMm) * sc;
        g += IM('basePlate', ux2 + upW / 2 - footW / 2, yG - footH, footW, footH);
        if (cr.frameProtectors !== 'none') {
          var gh = Math.min(Math.max(420 * sc, 16), 46), gw = upW * 2.0;
          g += IM('guard', ux2 + upW / 2 - gw / 2, yG - gh, gw, gh);
        }
      }
      if (cr.endBarriers !== 'no') {
        var bH = Math.min(Math.max(560 * sc, 22), 56), bWd = bH * 0.5;
        g += IM('barrier', Math.min(x0 + runMm * sc + 5, W - mr - bWd), yG - bH, bWd, bH);
        if (cr.endBarriers === 'double') g += IM('barrier', x0 - bWd - 5, yG - bH, bWd, bH);
      }

      g += svgDim(ml - 16, yG - hMm * sc, ml - 16, yG, hMm.toLocaleString(), true, stl);
      g += svgDim(x0 + upW, yG + 14, x0 + upW + beamMm * sc, yG + 14, beamMm.toLocaleString(), false, stl);
      if (extra > 0) g += '<text x="' + (W - mr) + '" y="' + (yG - hMm * sc / 2) + '" text-anchor="end" fill="' + org + '" font-family="IBM Plex Mono, monospace" font-size="11">+' + extra + ' bays</text>';
      var runTot = ((bays * beamMm + (bays + 1) * upMm) / 1000).toFixed(1);
      g += '<text x="' + ml + '" y="' + (mt - 8) + '" fill="' + stl + '" font-family="IBM Plex Mono, monospace" font-size="10" letter-spacing="2">FRONT ELEVATION · ' + runTot + 'M RUN</text>';
      if (result.warnings.some(function (w) { return w.key === 'beam_capacity' && (state.runs.length < 2 || w.runIndex === state.activeRunIndex); })) {
        g += '<text x="' + (W - mr) + '" y="' + (mt - 8) + '" text-anchor="end" fill="' + org + '" font-family="IBM Plex Mono, monospace" font-size="10">⚠ BEAM DUTY TO CONFIRM</text>';
      }
      return g + '</svg>';
    }

    /* ---- Multi-run: quiet inline context next to a stage heading —
       "Run 2 of 2", theme font, sentence case, neutral grey pill, no
       kicker-style orange/letterspacing. Empty string (no element at
       all) until a second run exists, same absence rule as every
       other multi-run affordance. ---- */
    function runBadgeHTML() {
      if (!(state.requestType === 'complete_run' && state.runs.length >= 2)) return '';
      return '<span class="rsi-uqb-v2__run-badge">Run ' + (state.activeRunIndex + 1) + ' of ' + state.runs.length + '</span>';
    }

    /* ---- Multi-run: compact tabs above the drawing. Absent (empty
       string, no wrapper element at all) until a second run exists —
       this is the piece that must render nothing for single-run. ---- */
    function runTabsHTML() {
      if (!(state.requestType === 'complete_run' && state.runs.length >= 2)) return '';
      var enterCls = runTabsEnterPending ? ' rsi-uqb-v2__runtabs--enter' : '';
      runTabsEnterPending = false;
      var h = '<div class="rsi-uqb-v2__runtabs' + enterCls + '">';
      state.runs.forEach(function (run, i) {
        h += '<button type="button" class="rsi-uqb-v2__runtab' + (i === state.activeRunIndex ? ' is-active' : '') + '" data-rsi-uqb-v2-run-switch="' + i + '">Run ' + (i + 1) + ' &middot; ' + run.bays + ' bays' +
          '<span class="rsi-uqb-v2__runtab-x" data-rsi-uqb-v2-run-remove="' + i + '" aria-label="Remove Run ' + (i + 1) + '">&times;</span></button>';
      });
      if (state.runs.length < 6) {
        h += '<button type="button" class="rsi-uqb-v2__runtab rsi-uqb-v2__runtab--add" data-rsi-uqb-v2-run-add aria-label="Add another run">+</button>';
      }
      h += '</div>';
      return h;
    }

    /* ============================================================
       LEDGER (title-block sheet). Only ONE "Project" cell in the head
       row now — the ref number is phase-1's own concern (it stamps
       [data-rsi-uqb-summary-desktop]'s first child), so the ledger
       doesn't try to render a second, unpopulated ref slot of its
       own; v2.css styles phase-1's stamp to read as this card's own
       header strip instead of loose text above it.
       ============================================================ */
    function lineItemRowHTML(li) {
      var priced = li.status === 'priced';
      return '<div class="rsi-uqb-v2__lrow' + (priced ? '' : ' rsi-uqb-v2__lrow--rev') + '"><span>' + li.label + ' (' + li.quantity + ')</span><span>' + (priced ? money(li.totalPence) : 'On the call') + '</span></div>';
    }

    function ledgerHTML(result) {
      var h = '<div class="rsi-uqb-v2__tblock"><div class="rsi-uqb-v2__ledger">';
      var multiRun = result.runs && result.runs.length >= 2;
      if (multiRun) {
        h += '<div class="rsi-uqb-v2__lheading">Your quote &middot; ' + result.runs.length + ' runs</div>';
        result.runs.forEach(function (run) {
          h += '<div class="rsi-uqb-v2__lgroup-head">' + esc(run.label) + ' &middot; ' + run.bays + ' bays</div>';
          run.lineItems.forEach(function (li) { h += lineItemRowHTML(li); });
          if (!run.lineItems.length) {
            h += '<div class="rsi-uqb-v2__lrow rsi-uqb-v2__lrow--rev"><span>No items yet</span><span></span></div>';
          }
          h += '<div class="rsi-uqb-v2__lgroup-sub"><span>' + esc(run.label) + ' subtotal</span><span>' + (run.isPartialEstimate ? 'Confirmed on your call' : money(run.subtotalPence)) + '</span></div>';
        });
      } else {
        result.lineItems.forEach(function (li) { h += lineItemRowHTML(li); });
        if (!result.lineItems.length) {
          h += '<div class="rsi-uqb-v2__lrow rsi-uqb-v2__lrow--rev"><span>Your items appear here as you build</span><span></span></div>';
        }
      }
      h += '<div class="rsi-uqb-v2__lsep"></div>';
      if (result.subtotalPence === 0) {
        h += '<div class="rsi-uqb-v2__ltbd"><b>Price confirmed on your call</b><span>This configuration needs a quick stock check — send it over and we’ll price it same day.</span></div>';
      } else if (result.isPartialEstimate) {
        h += '<div class="rsi-uqb-v2__ltbd"><b>GUIDE TOTAL' + (multiRun ? ' · ALL RUNS' : '') + ' — Confirmed on your call</b><span>Some items need a quick stock check — we’ll price everything on one call.</span></div>';
      } else {
        h += '<div class="rsi-uqb-v2__lrow rsi-uqb-v2__lrow--sub"><span>Subtotal, ex VAT</span><span>' + money(result.subtotalPence) + '</span></div>';
        h += '<div class="rsi-uqb-v2__lrow rsi-uqb-v2__lrow--sub"><span>VAT 20%</span><span>' + money(result.vatPence) + '</span></div>';
        h += '<div class="rsi-uqb-v2__ltotal"><small>GUIDE TOTAL' + (multiRun ? ' · ALL RUNS' : '') + ', INC VAT</small><b>' + money(result.totalIncVatPence) + '</b></div>';
      }
      h += '<div class="rsi-uqb-v2__lnote">Excludes delivery &amp; installation · confirmed with one call, never quietly changed.</div>';
      result.warnings.forEach(function (w) {
        if (w.key === 'delivery_excluded') return;
        h += '<div class="rsi-uqb-v2__lwarn">' + w.text + '</div>';
      });
      h += '</div></div>';
      return h;
    }

    /* ---- Step 1 "Not sure" contact card — shown in the sheet's photo
       area instead of a hero image, for people who'd rather just
       ring than go through the space-description flow. Email comes
       from the store's shop.email (never hardcoded); WhatsApp only
       appears if settings.social_whatsapp_url is configured. ---- */
    function contactCardHTML() {
      var email = root.getAttribute('data-rsi-uqb-v2-contact-email') || '';
      var whatsapp = root.getAttribute('data-rsi-uqb-v2-whatsapp-url') || '';
      var openingNote = root.getAttribute('data-rsi-uqb-v2-opening-note') || 'Open 7 days, 8am–5pm';
      var h = '<div class="rsi-uqb-v2__contact-card">';
      h += '<h3 class="rsi-uqb-v2__contact-heading">Talk to a human</h3>';
      h += '<p class="rsi-uqb-v2__contact-line">Tell us your space and what you’re storing — we’ll spec and price it with you.</p>';
      h += '<div class="rsi-uqb-v2__contact-rows">';
      h += '<div class="rsi-uqb-v2__contact-row">' +
        '<a class="rsi-uqb-v2__contact-btn rsi-uqb-v2__contact-btn--call" href="tel:01827949222">Call 01827 949222</a>' +
        '<span class="rsi-uqb-v2__contact-caption">' + esc(openingNote) + '</span></div>';
      if (email) {
        h += '<div class="rsi-uqb-v2__contact-row">' +
          '<a class="rsi-uqb-v2__contact-btn rsi-uqb-v2__contact-btn--email" href="mailto:' + esc(email) + '">Email us</a>' +
          '<span class="rsi-uqb-v2__contact-caption rsi-uqb-v2__contact-caption--select">' + esc(email) + '</span></div>';
      }
      if (whatsapp) {
        h += '<div class="rsi-uqb-v2__contact-row">' +
          '<a class="rsi-uqb-v2__contact-btn rsi-uqb-v2__contact-btn--email" href="' + esc(whatsapp) + '" target="_blank" rel="noopener">WhatsApp us</a>' +
          '<span class="rsi-uqb-v2__contact-caption">Send photos of your space — it helps</span></div>';
      }
      h += '</div>';
      h += '<p class="rsi-uqb-v2__contact-reassure">No obligation — a quick chat is usually all it takes to spec the right racking.</p>';
      h += '</div>';
      return h;
    }

    /* Same empty-cart line ledgerHTML() already shows when a normal
       ledger has zero line items — reused verbatim (same classes) so
       Step 1's neutral placeholder matches it visually, without
       pulling in a totals block. */
    function neutralLedgerHTML() {
      return '<div class="rsi-uqb-v2__tblock"><div class="rsi-uqb-v2__ledger"><div class="rsi-uqb-v2__lrow rsi-uqb-v2__lrow--rev"><span>Your items appear here as you build</span><span></span></div></div></div>';
    }

    /* ============================================================
       SHEET PANEL DISPATCH — single source of truth for what the
       right-hand sheet shows. Every caller reads sheetMode/drawHtml/
       ledgerMarkup from THIS function's return value; nothing else is
       allowed to compute or overwrite them.

       Why this exists: the previous version of refreshSheet() decided
       each panel with its own scattered condition — the decking/
       accessories branches correctly checked `stageIx > 0` before
       showing products, but the Step 1 hero branch checked stageIx
       for the PHOTO only and unconditionally called ledgerHTML(result)
       for the panel underneath it. Since state (e.g. state.framesOnly)
       stays populated after Back-navigating from a priced Step 3,
       that produced a fully priced ledger — or a "Price confirmed on
       your call" block — under the Step 1 hero. Two independent
       per-branch conditions (stage for the photo, none at all for the
       ledger) happened to agree often enough that the gap went
       unnoticed.

       Structuring it as one function keyed on (stage, type) — the two
       things that must gate what renders — makes "stage 1 never
       prices anything" a property of the dispatcher itself, not
       something every branch has to individually remember. ---- */
    function sheetPanel(stage, type, result) {
      if (stage === 0) {
        /* Stage 1: hero or contact card, nothing else, ever. */
        if (type === 'not_sure') {
          return { sheetMode: 'contact', drawHtml: contactCardHTML(), ledgerMarkup: '', productMode: null };
        }
        var hero = HEROES[type];
        return {
          sheetMode: hero ? 'photo' : 'empty',
          drawHtml: hero ? '<img class="rsi-uqb-v2__hero-img" src="' + hero + '" alt="' + esc(requestTypeLabel(type)) + '">' : '',
          ledgerMarkup: neutralLedgerHTML(),
          productMode: null
        };
      }

      /* Stage 2+: drawing+ledger / products / contact, per flow.
         productMode is read-only bookkeeping for the mbar's hint/CTA
         text (View drawing/products, Continue vs. the basket CTA) —
         it never feeds back into what gets rendered here. */
      if (type === 'decking_only') {
        var dOnly = state.deckingOnly;
        if (!dOnly.resultsShown) {
          /* Phase 1: answer questions, right panel stays quiet. Never
             even calls matchDeckProducts — no live re-filtering. */
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<div class="rsi-uqb-v2__deck-placeholder"><p>Answer four quick questions — we’ll work out exactly what you need.</p></div>', productMode: 'deck-placeholder' };
        }
        if (deckCatalogueLoading) {
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<p class="rsi-uqb-v2__hint" style="padding:16px 16px 0;">Loading deck options…</p>', productMode: 'deck-loading' };
        }
        if (isDeckingFallback(dOnly)) {
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<div class="rsi-uqb-v2__anno rsi-uqb-v2__anno--info" style="margin:16px 16px 0;"><b>INFO</b><span>Non-standard size — we’ll quote it same day.</span></div>' + ledgerHTML(result), productMode: 'deck-fallback' };
        }
        var deckMatches = matchDeckProducts(dOnly);
        if (!deckMatches.length) {
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<div class="rsi-uqb-v2__anno rsi-uqb-v2__anno--info" style="margin:16px 16px 0;"><b>INFO</b><span>Non-standard size — we’ll quote it same day.</span></div>' + ledgerHTML(result), productMode: 'deck-fallback' };
        }
        return { sheetMode: 'deckbasket', drawHtml: deckBasketBarHTML(), ledgerMarkup: deckResultsHTML(deckMatches, dOnly), productMode: 'deck-matched' };
      }

      if (type === 'accessories_only') {
        var aOnly = state.accessoriesOnly;
        if (!aOnly.resultsShown) {
          /* Phase 1: answer questions, right panel stays quiet. Never
             even calls matchAccessoryProducts — no live re-filtering. */
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<div class="rsi-uqb-v2__deck-placeholder"><p>Answer a couple of quick questions — we’ll work out exactly what you need.</p></div>', productMode: 'acc-placeholder' };
        }
        if (accessoriesCatalogueLoading) {
          return { sheetMode: 'empty', drawHtml: '', ledgerMarkup: '<p class="rsi-uqb-v2__hint" style="padding:16px 16px 0;">Loading options…</p>', productMode: 'acc-loading' };
        }
        var accMatches = matchAccessoryProducts(aOnly);
        if (accMatches.length) {
          return { sheetMode: 'deckbasket', drawHtml: accessoryBasketBarHTML(), ledgerMarkup: accessoryResultsHTML(accMatches, aOnly), productMode: 'acc-matched' };
        }
        var accCategory = aOnly.category;
        var accField = ACCESSORY_FALLBACK_FIELD[accCategory];
        var accFieldLabels = { floor_bolts: 'How many floor bolts?', leg_protectors: 'How many leg protectors?', barriers: 'How many barriers?', load_notices: 'How many load notice boards?' };
        return {
          sheetMode: 'empty', drawHtml: '',
          ledgerMarkup: '<div class="rsi-uqb-v2__anno rsi-uqb-v2__anno--info" style="margin:16px 16px 0;"><b>INFO</b><span>' + esc(ACCESSORY_FALLBACK_NOTES[accCategory]) + '</span></div>' +
            (accField ? '<div style="margin:16px 16px 0;">' + stepperHTML(accField, 0, 999, 'rsi-uqb-v2-ao-fallback-qty', accFieldLabels[accCategory]) + '</div>' : '') +
            ledgerHTML(result),
          productMode: 'acc-fallback'
        };
      }

      /* complete_run, frames_only, beams_only, not_sure (stage 2+):
         live drawing + priced ledger. */
      return { sheetMode: 'drawing', drawHtml: runTabsHTML() + drawSvg(result), ledgerMarkup: ledgerHTML(result), productMode: null };
    }

    /* ---- Lightweight refresh: ledger + drawing + payload only — used
       on every keystroke in a text field so focus/cursor position is
       never disturbed. Full render() (below) is used for anything
       that changes what the CURRENT stage itself should show. ---- */
    function refreshSheet() {
      var result = calculateGuideEstimate(state);
      var panel = sheetPanel(stageIx, state.requestType, result);

      if (summaryDesktop) summaryDesktop.innerHTML = panel.ledgerMarkup;
      if (summaryMobile) summaryMobile.innerHTML = panel.ledgerMarkup;
      [drawDesktop, drawMobile].forEach(function (el) {
        if (!el) return;
        el.className = 'rsi-uqb-v2__draw rsi-uqb-v2__draw--' + panel.sheetMode;
        el.innerHTML = panel.drawHtml;
      });
      if (payloadField) payloadField.value = JSON.stringify(buildJsonPayload(state, result, pageHandle));
      if (bodyField) bodyField.value = buildHumanReadablePayload(state, result);

      var matched = panel.productMode === 'deck-matched' || panel.productMode === 'acc-matched';
      var noProductPanel = panel.productMode == null;

      var mobileViewHint = root.querySelector('[data-rsi-uqb-v2-mbar-hint]');
      if (mobileViewHint) mobileViewHint.textContent = matched ? 'View products ›' : (noProductPanel ? 'View drawing ›' : 'View details ›');

      if (mbarCtaHost) mbarCtaHost.innerHTML = mbarCtaHTML(panel.productMode);

      var mbarTot = root.querySelector('[data-rsi-uqb-v2-mbar-total]');
      if (mbarTot) {
        if (panel.productMode === 'deck-matched') {
          var s = deckCartSummary();
          mbarTot.textContent = s.count ? (s.count + ' deck' + (s.count === 1 ? '' : 's') + ' · ' + deckMoney(s.totalGBP)) : 'Add a deck';
        } else if (panel.productMode === 'deck-placeholder') {
          mbarTot.textContent = 'Answer the questions →';
        } else if (panel.productMode === 'acc-matched') {
          var sa = accessoryCartSummary();
          mbarTot.textContent = sa.count ? (sa.count + ' item' + (sa.count === 1 ? '' : 's') + ' · ' + deckMoney(sa.totalGBP)) : 'Add an item';
        } else if (panel.productMode === 'acc-placeholder') {
          mbarTot.textContent = 'Answer the questions →';
        } else if (stageIx === 0) {
          mbarTot.textContent = 'On your call';
        } else {
          mbarTot.textContent = (result.subtotalPence === 0 || result.isPartialEstimate) ? 'On your call' : money(result.totalIncVatPence);
        }
      }
      if (!interactionStarted) { interactionStarted = true; if (mobileBar) mobileBar.hidden = false; }
      return result;
    }

    /* ---- Full render: regenerates the current stage's markup (only
       stage in the DOM), the rail and the sheet. ---- */
    function render() {
      var f = flow();
      if (stageIx > f.length - 1) stageIx = f.length - 1;
      if (stageIx < 0) stageIx = 0;
      var id = f[stageIx];
      if (stagePane) stagePane.innerHTML = STAGES[id]() + navHTML();
      renderRail();
      refreshSheet();
      document.dispatchEvent(new CustomEvent('rsiuqb:stage', { detail: { stage: id } }));
    }

    function goToStage(ix) {
      stageIx = ix;
      render();
      /* Scroll the OUTER .rsi-uqb-v2__stage into view, not the inner
         stage-pane — the pane has no scroll-margin-top of its own, so
         scrolling it flush to the viewport top pushed the progress
         rail (a sibling above the pane) off-screen and let the fixed
         mobile header cover the new step's own heading. The outer
         element already carries the correct header-height offset in
         CSS; this just makes the scroll target match it. */
      var scrollTarget = stageOuter || stagePane;
      if (scrollTarget) scrollTarget.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    /* "Deck another size" — resets Phase 1 answers back to defaults,
       keeps the qty-stepper memory and the real Shopify cart intact.
       Safe to reassign state.deckingOnly wholesale: nothing else
       keeps a separately-cached reference to it. */
    function resetDeckPhase1() {
      var fresh = defaultDeckingOnly();
      fresh.cartQty = state.deckingOnly.cartQty;
      state.deckingOnly = fresh;
      render();
    }

    /* ---- Multi-run: run management. state.completeRun is always
       kept as the SAME object reference as state.runs[activeRunIndex]
       — every add/switch/remove below only ever repoints it at a
       different array element, never replaces it with a new literal,
       so every existing per-run field/stage/drawing keeps working
       unmodified. ---- */
    function addRun() {
      if (state.runs.length >= 6) return;
      var fromRunNumber = state.activeRunIndex + 1;
      var copy = JSON.parse(JSON.stringify(state.completeRun));
      state.runs.push(copy);
      state.activeRunIndex = state.runs.length - 1;
      state.completeRun = copy;
      /* Feedback on click is the point — never a silent jump. Banner
         is reactive (STAGES.size checks newRun === activeRunIndex+1)
         and the tabs get exactly one entrance animation, consumed by
         runTabsHTML() the next time it renders. */
      state.runAddedBanner = { newRun: state.runs.length, fromRun: fromRunNumber };
      runTabsEnterPending = true;
      goToStage(flow().indexOf('size'));
    }
    function switchRun(ix) {
      if (ix === state.activeRunIndex || !state.runs[ix]) return;
      state.activeRunIndex = ix;
      state.completeRun = state.runs[ix];
      render();
    }
    function removeRun(ix) {
      if (state.runs.length <= 1 || !state.runs[ix]) return;
      if (!window.confirm('Remove Run ' + (ix + 1) + '? This can’t be undone.')) return;
      state.runs.splice(ix, 1);
      state.runAddedBanner = null;
      if (state.activeRunIndex === ix) {
        state.activeRunIndex = Math.min(ix, state.runs.length - 1);
        state.completeRun = state.runs[state.activeRunIndex];
      } else {
        state.activeRunIndex = state.runs.indexOf(state.completeRun);
      }
      render();
    }

    /* ============================================================
       EVENT DELEGATION — the stage pane's own content is destroyed
       and recreated on every render(), so every listener lives on
       the persistent root, matched via event.target.closest().
       ============================================================ */
    root.addEventListener('click', function (event) {
      var t = event.target;
      var goEl = t.closest('[data-rsi-uqb-v2-go]');
      if (goEl) { goToStage(Number(goEl.getAttribute('data-rsi-uqb-v2-go'))); return; }
      var backEl = t.closest('[data-rsi-uqb-v2-nav-back]');
      if (backEl) { goToStage(Math.max(0, stageIx - 1)); return; }
      var nextEl = t.closest('[data-rsi-uqb-v2-nav-next]');
      if (nextEl) { goToStage(Math.min(flow().length - 1, stageIx + 1)); return; }
      /* Mobile bar's basket CTA (shop-flow matched mode, nothing in
         the cart yet) — opens the same mobile sheet the total-tap
         does, since the per-product Add buttons live there. Declared
         with var below (hoisted), assigned by the time this fires. */
      var mbarOpenSheetEl = t.closest('[data-rsi-uqb-v2-mbar-open-sheet]');
      if (mbarOpenSheetEl) {
        if (mobileSummaryPanel) mobileSummaryPanel.hidden = false;
        if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
        return;
      }
      var runAddEl = t.closest('[data-rsi-uqb-v2-run-add]');
      if (runAddEl) { addRun(); return; }
      var runBannerDismissEl = t.closest('[data-rsi-uqb-v2-run-banner-dismiss]');
      if (runBannerDismissEl) { state.runAddedBanner = null; render(); return; }
      /* Remove checked before switch: the x sits nested inside the
         switchable tab button, so both selectors would match a click
         on it — remove must win. */
      var runRemoveEl = t.closest('[data-rsi-uqb-v2-run-remove]');
      if (runRemoveEl) { removeRun(Number(runRemoveEl.getAttribute('data-rsi-uqb-v2-run-remove'))); return; }
      var runSwitchEl = t.closest('[data-rsi-uqb-v2-run-switch]');
      if (runSwitchEl) { switchRun(Number(runSwitchEl.getAttribute('data-rsi-uqb-v2-run-switch'))); return; }
      var stepDown = t.closest('[data-rsi-uqb-step-down]');
      var stepUp = t.closest('[data-rsi-uqb-step-up]');
      if (stepDown || stepUp) {
        var stepper = (stepDown || stepUp).closest('[data-rsi-uqb-stepper]');
        var input = stepper.querySelector('input[data-rsi-uqb-field]');
        var deckQtyInput = stepper.querySelector('input[data-rsi-uqb-v2-deck-qty]');
        var min = Number((input || deckQtyInput).getAttribute('min')) || 0, max = Number((input || deckQtyInput).getAttribute('max')) || 999;
        if (input) {
          var path = input.getAttribute('data-rsi-uqb-field');
          var v = (parseInt(input.value, 10) || min) + (stepUp ? 1 : -1);
          if (v < min) v = min;
          if (v > max) v = max;
          stateSet(path, v);
          render();
          return;
        }
        if (deckQtyInput) {
          var variantId = Number(deckQtyInput.getAttribute('data-rsi-uqb-v2-deck-qty'));
          var v2 = (parseInt(deckQtyInput.value, 10) || 0) + (stepUp ? 1 : -1);
          if (v2 < min) v2 = min;
          if (v2 > max) v2 = max;
          activeCartQty()[variantId] = v2;
          render();
          return;
        }
      }
      var deckAddEl = t.closest('[data-rsi-uqb-v2-deck-add]');
      if (deckAddEl) {
        var addVariantId = Number(deckAddEl.getAttribute('data-rsi-uqb-v2-deck-add'));
        var addQty = Number(activeCartQty()[addVariantId]) || 1;
        if (addQty > 0) addProductToCart(addVariantId, addQty, deckAddEl.closest('.rsi-uqb-v2__deck-card'));
        return;
      }
      var tradeLinkEl = t.closest('[data-rsi-uqb-v2-trade-link]');
      if (tradeLinkEl) {
        event.preventDefault();
        goToStage(flow().length - 1);
        return;
      }
      var keepAddingEl = t.closest('[data-rsi-uqb-v2-deck-keep-adding]');
      if (keepAddingEl) {
        var cardsHost = summaryDesktop && summaryDesktop.offsetParent ? summaryDesktop : summaryMobile;
        if (cardsHost) cardsHost.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      var deckShowResultsEl = t.closest('[data-rsi-uqb-v2-deck-show-results]');
      if (deckShowResultsEl) { state.deckingOnly.resultsShown = true; render(); return; }
      /* Checked before the (nonexistent, but future-safe) card-level
         click targets: this button is a sibling of the alt cards,
         never nested inside one, so there's no closest() ambiguity. */
      var deckAltToggleEl = t.closest('[data-rsi-uqb-v2-deck-alt-toggle]');
      if (deckAltToggleEl) { state.deckingOnly.altExpanded = !state.deckingOnly.altExpanded; render(); return; }
      var deckAnotherEl = t.closest('[data-rsi-uqb-v2-deck-another]');
      if (deckAnotherEl) { resetDeckPhase1(); return; }
      var accShowResultsEl = t.closest('[data-rsi-uqb-v2-acc-show-results]');
      if (accShowResultsEl) { state.accessoriesOnly.resultsShown = true; render(); return; }
      var accAltToggleEl = t.closest('[data-rsi-uqb-v2-acc-alt-toggle]');
      if (accAltToggleEl) { state.accessoriesOnly.altExpanded = !state.accessoriesOnly.altExpanded; render(); return; }
      var accAddElseEl = t.closest('[data-rsi-uqb-v2-acc-add-else]');
      if (accAddElseEl) {
        state.accessoriesOnly.resultsShown = false;
        state.accessoriesOnly.altExpanded = false;
        state.accessoriesOnly.confirmedAdds = {};
        render();
        return;
      }
    });

    var CUSTOM_NUMERIC_BOUNDS = {
      'cr.frameHeightMm': { min: 1500, max: 15000 },
      'cr.beamLengthKey': { min: 900, max: 4000 },
      'cr.beamLevels': { min: 6, max: 10 },
      'fo.frameHeightMm': { min: 1500, max: 15000 },
      'bo.beamLengthKey': { min: 900, max: 4000 },
      'do.beamLengthKey': { min: 900, max: 4000 }
    };

    root.addEventListener('change', function (event) {
      var el = event.target;
      if (!el.matches('[data-rsi-uqb-field]')) return;
      var path = el.getAttribute('data-rsi-uqb-field');
      if (path === 'acknowledged') { state.acknowledgedGuideEstimate = el.checked; refreshSheet(); return; }
      if (el.type === 'radio' || el.type === 'checkbox') {
        var customToggle = el.getAttribute('data-rsi-uqb-v2-custom-toggle');
        if (customToggle) { stateSet(customToggle, true); render(); return; }
        var clearCustom = el.getAttribute('data-rsi-uqb-v2-clear-custom');
        if (clearCustom) stateSet(clearCustom, false);
        var val = el.type === 'checkbox' ? el.checked : el.value;
        if (path === 'requestType') {
          stateSet(path, val);
          stageIx = 0;
          /* Force a genuinely clean slate on every flow switch — a
             stale fragment from whichever flow was previously active
             (e.g. an old ledger price row) must never linger under
             the freshly rendered content, regardless of how the two
             layouts differ structurally. */
          if (summaryDesktop) summaryDesktop.innerHTML = '';
          if (summaryMobile) summaryMobile.innerHTML = '';
          if (drawDesktop) drawDesktop.innerHTML = '';
          if (drawMobile) drawMobile.innerHTML = '';
          render();
          return;
        }
        if (path === 'cr.onRacking') {
          stateSet(path, val);
          var derive = {
            uk_standard: { palletType: 'uk_standard', decking: 'none' },
            euro: { palletType: 'euro', decking: 'none' },
            timber: { decking: 'timber' },
            chipboard: { decking: 'chipboard' },
            mesh: { decking: 'mesh' },
            none: { decking: 'none' }
          }[val];
          if (derive) {
            if (derive.palletType) stateSet('cr.palletType', derive.palletType);
            if (derive.decking) stateSet('cr.decking', derive.decking);
          }
          render();
          return;
        }
        stateSet(path, val);
        render();
        return;
      }
      var val2 = el.value;
      var bounds = CUSTOM_NUMERIC_BOUNDS[path];
      if (bounds) {
        var n = parseInt(val2, 10);
        if (!isFinite(n)) n = bounds.min;
        n = Math.max(bounds.min, Math.min(bounds.max, n));
        val2 = n;
      }
      stateSet(path, val2);
      render();
    });

    root.addEventListener('input', function (event) {
      var el = event.target;
      revalidateFieldLive(el);
      if (el.hasAttribute('data-rsi-uqb-v2-deck-qty')) {
        var digitsOnlyQty = el.value.replace(/[^\d]/g, '');
        if (digitsOnlyQty !== el.value) el.value = digitsOnlyQty;
        activeCartQty()[Number(el.getAttribute('data-rsi-uqb-v2-deck-qty'))] = digitsOnlyQty === '' ? 0 : parseInt(digitsOnlyQty, 10);
        return;
      }
      if (!el.matches('[data-rsi-uqb-field]')) return;
      if (el.type === 'radio' || el.type === 'checkbox') return;
      var path = el.getAttribute('data-rsi-uqb-field');
      if (path === 'acknowledged') return;
      if (el.hasAttribute('data-rsi-uqb-v2-custom-numeric')) {
        var digitsOnly = el.value.replace(/[^\d]/g, '');
        if (digitsOnly !== el.value) el.value = digitsOnly;
        var liveBounds = CUSTOM_NUMERIC_BOUNDS[path];
        if (liveBounds && digitsOnly !== '' && parseInt(digitsOnly, 10) > liveBounds.max) {
          el.value = String(liveBounds.max);
        }
      }
      stateSet(path, el.value);
      refreshSheet();
    });

    function revalidateFieldLive(target) {
      if (!target || typeof target.id !== 'string' || !target.id) return;
      var errorEl = document.getElementById(target.id + '-error');
      if (!errorEl || errorEl.hidden) return;
      var stillInvalid = false;
      if (target.id === 'rsi-uqb-v2-name') stillInvalid = !target.value.trim();
      else if (target.id === 'rsi-uqb-v2-email') stillInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target.value.trim());
      else if (target.id === 'rsi-uqb-v2-postcode') stillInvalid = !target.value.trim();
      else if (target.id === 'rsi-uqb-v2-acknowledged') stillInvalid = !target.checked;
      else return;
      if (!stillInvalid) clearFieldError(target);
    }

    /* ---- Mobile summary expand/collapse ---- */
    var mobileToggle = root.querySelector('[data-rsi-uqb-mobile-summary-toggle]');
    var mobileSummaryPanel = root.querySelector('[data-rsi-uqb-summary-mobile-panel]');
    if (mobileToggle && mobileSummaryPanel) {
      mobileToggle.addEventListener('click', function () {
        var isOpen = mobileSummaryPanel.hidden === false;
        mobileSummaryPanel.hidden = isOpen;
        mobileToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
      mobileSummaryPanel.addEventListener('click', function (ev) { if (ev.target === mobileSummaryPanel) mobileSummaryPanel.hidden = true; });
      document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && mobileSummaryPanel.hidden === false) {
          mobileSummaryPanel.hidden = true;
          mobileToggle.setAttribute('aria-expanded', 'false');
          mobileToggle.focus();
        }
      });
    }

    /* ---- Validation ---- */
    function validate() {
      var errors = [];
      var nameEl = root.querySelector('[data-rsi-uqb-field="customer.name"]');
      var emailEl = root.querySelector('[data-rsi-uqb-field="customer.email"]');
      var postcodeEl = root.querySelector('[data-rsi-uqb-field="customer.postcode"]');
      var ackEl = root.querySelector('[data-rsi-uqb-field="acknowledged"]');

      if (!nameEl || !nameEl.value.trim()) errors.push({ field: nameEl, message: 'Please enter your name.' });
      var emailVal = emailEl ? emailEl.value.trim() : '';
      if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) errors.push({ field: emailEl, message: 'Please enter a valid email address.' });
      if (!postcodeEl || !postcodeEl.value.trim()) errors.push({ field: postcodeEl, message: 'Please enter a postcode.' });
      if (!ackEl || !ackEl.checked) errors.push({ field: ackEl, message: 'Please confirm you understand this is a guide estimate.' });
      return errors;
    }

    function addDescribedByToken(field, token) {
      if (!field || !token) return;
      var existing = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (existing.indexOf(token) === -1) existing.push(token);
      field.setAttribute('aria-describedby', existing.join(' '));
    }
    function removeDescribedByToken(field, token) {
      if (!field || !token) return;
      var existing = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      var next = existing.filter(function (t) { return t !== token; });
      if (next.length) field.setAttribute('aria-describedby', next.join(' '));
      else field.removeAttribute('aria-describedby');
    }
    function fieldErrorId(field) { return field && field.id ? field.id + '-error' : null; }
    function showFieldError(field, message) {
      if (!field) return;
      var errorId = fieldErrorId(field);
      if (!errorId) return;
      var errorEl = document.getElementById(errorId);
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      addDescribedByToken(field, errorId);
    }
    function clearFieldError(field) {
      if (!field) return;
      var errorId = fieldErrorId(field);
      if (!errorId) return;
      var errorEl = document.getElementById(errorId);
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      field.removeAttribute('aria-invalid');
      removeDescribedByToken(field, errorId);
    }
    function showValidationErrors(errors) {
      goToStage(flow().length - 1);
      errors.forEach(function (e) { showFieldError(e.field, e.message); });
      if (!errorSummary) return;
      errorSummary.hidden = false;
      errorSummary.innerHTML = '';
      var heading = document.createElement('p');
      heading.className = 'rsi-uqb-v2__error-summary-heading';
      heading.textContent = 'Please fix the following before sending your request:';
      errorSummary.appendChild(heading);
      var list = document.createElement('ul');
      errors.forEach(function (e) { var li = document.createElement('li'); li.textContent = e.message; list.appendChild(li); });
      errorSummary.appendChild(list);
      errorSummary.setAttribute('role', 'alert');
      if (errors[0] && errors[0].field) errors[0].field.focus();
      errorSummary.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    function clearValidationErrors() {
      root.querySelectorAll('.rsi-uqb-v2__field-error:not([hidden])').forEach(function (errorEl) {
        errorEl.hidden = true;
        var fieldId = errorEl.id.replace(/-error$/, '');
        var field = document.getElementById(fieldId);
        if (field) { field.removeAttribute('aria-invalid'); removeDescribedByToken(field, errorEl.id); }
        errorEl.textContent = '';
      });
      if (!errorSummary) return;
      errorSummary.hidden = true;
      errorSummary.innerHTML = '';
    }

    /* ---- Submit: identical pending-marker pattern to previous builds.
       Deliberately "formEl" (the real <form>) here, not "form" (the
       "[data-rsi-uqb-form]" wrapper div nested INSIDE it) — a listener
       attached to "form" never fires at all, since a "submit" event's
       target is the <form> element and it only bubbles UP through
       ancestors, never down into a descendant. That bug meant this
       whole block — validation, disabling the button, and setting the
       pending-submission marker — silently never ran on a real
       submission, and separately, form.id below was always "" (the
       wrapper div carries no id), so even a corrected listener would
       still have failed the id match against pending.formId every
       time. Together this is why a genuine successful submission
       never showed the receipt screen: the page just reloaded back to
       the wizard's default first-stage state with nothing to say a
       request had gone anywhere — confirmed by simulating a real
       posted_successfully response and observing the success panel
       stay hidden. ---- */
    if (formEl) {
      formEl.addEventListener('submit', function (event) {
        clearValidationErrors();
        var errors = validate();
        if (errors.length) { event.preventDefault(); showValidationErrors(errors); return; }

        refreshSheet();

        var submitBtn = formEl.querySelector('[data-rsi-uqb-submit]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }

        try { window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ formId: formEl.id, ts: Date.now() })); } catch (e) {}
      });
    }

    (function resolveSuccessState() {
      var postedMarker = root.querySelector('[data-rsi-uqb-posted]');
      var postedValue = postedMarker ? postedMarker.getAttribute('data-rsi-uqb-posted') : '';
      var isOurPendingSubmission = false;
      try {
        var pendingRaw = window.sessionStorage.getItem(PENDING_KEY);
        if (pendingRaw) {
          window.sessionStorage.removeItem(PENDING_KEY);
          var pending = JSON.parse(pendingRaw);
          var pendingFresh = pending && typeof pending.ts === 'number' && (Date.now() - pending.ts) < PENDING_MAX_AGE_MS;
          isOurPendingSubmission = !!(pendingFresh && formEl && pending.formId && pending.formId === formEl.id);
        }
      } catch (e) {}

      if (postedValue === 'true' && isOurPendingSubmission) {
        /* Microsoft Ads (UET) conversion signal. Reached only when the server
           rendered posted_successfully? as true AND the fresh sessionStorage
           marker belonged to this exact form — the marker is deleted above as
           it is read, so a reload, a bare ?contact_posted=true URL, reaching
           the final step, or clicking submit cannot get here. */
        fireLeadConversion(formEl && formEl.id, 'native');
        if (successPanel) { successPanel.hidden = false; successPanel.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
        if (formEl) formEl.hidden = true;
        /* The rest of the wizard chrome (progress stepper, drawing +
           price panel, mobile bottom bar) all live OUTSIDE the <form>,
           so hiding formEl alone leaves them showing right next to the
           receipt — stepper still highlighting step 1 in particular
           reads as "back at the start" even with a clear success
           message right above it. Hide them all so this genuinely
           reads as one finished page, not a wizard sitting mid-reset
           next to a message. */
        var stepper = document.querySelector('.rsi-uqb-v2__stagestepper');
        if (stepper) stepper.hidden = true;
        var sheetwrap = document.querySelector('.rsi-uqb-v2__sheetwrap');
        if (sheetwrap) sheetwrap.hidden = true;
        var mobileBar = document.querySelector('[data-rsi-uqb-mobile-bar]');
        if (mobileBar) mobileBar.hidden = true;
        var mobileSheet = document.querySelector('[data-rsi-uqb-summary-mobile-panel]');
        if (mobileSheet) mobileSheet.hidden = true;
      } else if (postedValue === 'false' && isOurPendingSubmission) {
        showValidationErrors([{ field: null, message: 'Something went wrong sending your request. Please try again or call 01827 949222.' }]);
        var submitBtn2 = formEl && formEl.querySelector('[data-rsi-uqb-submit]');
        if (submitBtn2) { submitBtn2.disabled = false; submitBtn2.textContent = root.getAttribute('data-rsi-uqb-v2-primary-cta') || 'Send my quote request'; }
      }
    })();

    render();
  }

  function init() {
    document.querySelectorAll('[data-rsi-uqb]').forEach(function (root) {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      initializeBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector) {
      var root = event.target.querySelector('[data-rsi-uqb]');
      if (root) init();
    }
  });
})();
